/**
 * Photo-to-Art tool — standalone lead-capture flow.
 * Lives at /pages/photo-to-art. Reuses window.instameIdentify for Klaviyo +
 * Supabase identification, and the existing photo upload + AI generation
 * pipeline in instame-shop-backend.
 *
 * Share view mode is triggered by ?view=<result_id> on the URL — the friend
 * who clicks the link lands on the same page but sees the result image only,
 * with a CTA to make their own.
 */
(function () {
  "use strict";

  // ── Section root ────────────────────────────────────────────────────
  //
  // Use the .p2a class as the section root — Shopify's own cart-items wrapper
  // also exposes [data-section-id] earlier in the DOM, so a generic selector
  // would silently match the wrong element and our queries would miss.
  //
  // Read up here (rather than beside the other root queries below) because the
  // storage keys and event properties in the Config block depend on it.

  const root = document.querySelector(".p2a");
  if (!root) return;

  // ── Config ──────────────────────────────────────────────────────────

  // Optional per-page namespace, set by the section markup. The photo-to-art
  // section sets none, so TOOL_ID is "" there and every key/property derived
  // from it below is byte-identical to what it was before this existed.
  // sections/exclusive-designs.liquid sets "exclusive" so that tool keeps its
  // own saved progress and its own randomized display plan — sharing them
  // would resume a visitor who already finished a photo-to-art portrait
  // straight to that old result, so they'd never see the design on offer.
  const TOOL_ID = root.getAttribute("data-p2a-tool-id") || "";
  const KEY_SUFFIX = TOOL_ID ? "_" + TOOL_ID : "";

  // Every surface that reaches the backend is still the photo-to-art tool:
  // api.photos.upload, api.photo-to-art.generate/status/attach-email, the
  // delivery email and the job-timeout sweeper all filter on this exact
  // value. It identifies the pipeline, not the page — TOOL_ID does that.
  const SOURCE = "photo_to_art_tool";
  const API_BASE =
    (/(^|\.)(instame\.shop|lilaland\.co|lilaland\.pet)$/i.test(location.hostname) || /\.myshopify\.com$/i.test(location.hostname))
      ? "https://api.instame.shop"
      : (location.hostname === "localhost" || location.hostname === "127.0.0.1")
        ? "http://localhost:3000" // direct `shopify theme dev` → local backend
        : ""; // other non-prod hosts (Tailscale/phone) → same-origin dev gateway

  const POLL_INTERVAL_MS = 1500;
  // 10 minutes — long enough for slow watermark composites and bursty
  // backend latency. The user can navigate away at any point; persistence
  // resumes them on the next visit.
  const POLL_TIMEOUT_MS = 10 * 60 * 1000;
  const STATE_LOOKUP_RETRY_MS = 3000;

  // Order matches steps in the section markup. Style first — leading with
  // the gallery hooks people before the upload ask.
  const STEPS = ["style", "upload", "name", "email", "generating", "result"];

  // Public-facing slugs used in the URL (?step=…) and in the GTM dataLayer
  // event names (p2a_<slug_with_underscores>). Decoupled from internal step
  // names so we can rename internals without breaking marketing triggers.
  const STEP_SLUGS = {
    style: "choose-a-style",
    upload: "upload-photo",
    name: "your-pet-name",
    email: "your-email",
    generating: "generating",
    result: "your-art",
  };

  // Set during boot. Share-view mode owns the URL (?share=…) and is not part
  // of the funnel, so we suppress step URL/event updates there.
  let isShareView = false;

  // iPadOS 13+ reports as MacIntel — the maxTouchPoints check disambiguates.
  function isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  }
  function isAndroid() {
    return /Android/i.test(navigator.userAgent);
  }

  // Pre-written caption used by X / Pinterest / Reddit / native share. FB
  // ignores it (sharer.php strips text params; previews come from OG tags).
  // Built dynamically so the caption mentions the actual pet name + style.
  function buildShareCopy() {
    const label = state.styleName ? labelForStyle(state.styleName) : null;
    const name = state.petName;
    if (label && name) {
      return `Look at this ${label} of ${name} — made free with LilaLand 🐾`;
    }
    if (label) {
      return `Just made a ${label} of my pet with LilaLand — try it free 🐾`;
    }
    return "Just made a portrait of my pet with LilaLand — try it free, see how yours would look 🐾";
  }

  const LOADING_FACTS = [
    "Hand-trained on thousands of pet portraits — yours takes about 20 seconds.",
    "Adding the finishing brushstrokes…",
    "Finding the perfect lighting…",
    "Almost ready — turning your photo into art.",
  ];

  // ── Helpers ─────────────────────────────────────────────────────────

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) =>
    Array.from((root || document).querySelectorAll(sel));

  function getQueryParam(name) {
    return new URLSearchParams(location.search).get(name);
  }

  function getOrCreateUserId() {
    const existing = localStorage.getItem("instame_user_id");
    if (existing) return existing;
    const fresh = "anon_" + crypto.randomUUID();
    localStorage.setItem("instame_user_id", fresh);
    return fresh;
  }

  function getOrCreateSessionId() {
    const existing = sessionStorage.getItem("instame_session_id");
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem("instame_session_id", fresh);
    return fresh;
  }

  // Validators are shared with instant-preview via window.LeadCapture
  // (assets/lead-capture.js, loaded before this script in the section).
  const isValidEmail = (v) => window.LeadCapture.isValidEmail(v);
  const isValidPhone = (digits, country) =>
    window.LeadCapture.isValidPhone(digits, country);
  // Guarded like suggestEmail below: an older cached lead-capture.js may
  // predate normalizeNsn, and the email step must never break on that.
  const normalizeNsn = (digits, country) =>
    typeof window.LeadCapture.normalizeNsn === "function"
      ? window.LeadCapture.normalizeNsn(digits, country)
      : digits;

  function showError(scope, message) {
    const el = document.querySelector(`[data-p2a-error="${scope}"]`);
    if (!el) return;
    // A prior non-blocking notice may have neutralized the color — real
    // errors (and clears) restore the stylesheet's error styling.
    el.style.removeProperty("color");
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = "";
      el.hidden = true;
    }
  }

  // Same slot as showError, but styled as plain text — for FYI messages
  // that must not read as a blocking error.
  function showNotice(scope, message) {
    showError(scope, message);
    const el = document.querySelector(`[data-p2a-error="${scope}"]`);
    if (el && message) {
      el.style.color = "inherit";
    }
  }

  // PostHog's replay session id (a UUIDv7 minted by posthog-js). Guarded:
  // before posthog-js finishes loading, the stub's get_session_id returns
  // undefined and the event simply goes unlinked.
  function phSessionId() {
    try {
      if (window.posthog && typeof window.posthog.get_session_id === "function") {
        const s = window.posthog.get_session_id();
        if (typeof s === "string" && s) {
          return s;
        }
      }
    } catch (_) {}
    return null;
  }

  // Fire a PostHog-only analytics event (no Supabase write). Reserved for
  // p2a-tool funnel + error pings — Supabase's user_events stays scoped to
  // main-store actions. Fire-and-forget; failures never affect user flow.
  // _fbp is a first-party cookie written by fbevents.js, which Shopify's
  // Facebook & Instagram pixel loads. Absent is fine — Meta just scores the
  // match lower.
  function readFbp() {
    try {
      return (document.cookie.match(/(?:^|;\s*)_fbp=([^;]+)/) || [])[1] || null;
    } catch (_) {
      return null;
    }
  }

  function trackP2A(event, properties) {
    try {
      const userId = state.userId || localStorage.getItem("instame_user_id");
      if (!userId) return;
      const replaySessionId = phSessionId();
      fetch(`${API_BASE}/api/events/posthog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          event,
          // Meta's Conversions API rejects website events without an
          // event_source_url, and _fbp materially improves match quality.
          // Top-level, not in `properties`: properties is mirrored to
          // PostHog and an ad identifier does not belong there.
          page_url: location.href,
          fbp: readFbp(),
          // Ride the active A/B variants (exp_* keys) on every P2A event so they
          // can be sliced by experiment in PostHog / Supabase. `tool` splits the
          // two funnels that share this script: the photo-to-art page and the
          // single-design exclusive page. $session_id links the server-captured
          // event to the session replay.
          properties: Object.assign(
            { tool: TOOL_ID || "photo_to_art" },
            window.instameExperiments || {},
            replaySessionId ? { "$session_id": replaySessionId } : {},
            properties || {}
          ),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) {}
  }

  // ── Persistence helpers ─────────────────────────────────────────────

  const STORAGE_KEY = "instame_p2a_state" + KEY_SUFFIX;
  const STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      if (
        typeof parsed.savedAt !== "number" ||
        Date.now() - parsed.savedAt > STORAGE_TTL_MS
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      console.warn("[photo-to-art] failed to load state", e);
      return null;
    }
  }

  function saveState(partial) {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const existing =
        parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? parsed
          : {};
      const next = { ...existing, ...partial, savedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      console.warn("[photo-to-art] failed to save state", e);
    }
  }

  function clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  // Marketing "start over" link (?fresh=1). Email/SMS campaigns announcing a
  // new style point here so the recipient lands on the style picker instead
  // of being resumed into a half-finished — or already-finished — run. Without
  // it, the style they were just told about is the one screen they never see:
  // they'd open the link and get their old portrait back, which reads as a
  // broken promo rather than a saved session.
  //
  // Read once at boot, so consumeFreshParam() can strip it from the address
  // bar without changing this page's behaviour mid-session.
  //
  // Deliberately lenient: any present value except "0" counts, so a campaign
  // link hand-written as ?fresh or ?fresh=true still works. A strict "1"-only
  // check would fail silently, and the failure mode is exactly the stale
  // resume this flag exists to prevent. The pre-paint scripts in
  // sections/photo-to-art.liquid and sections/exclusive-designs.liquid use
  // the same rule — they must agree or the first paint fights the JS.
  const isFreshStart = (() => {
    const raw = getQueryParam("fresh");
    return raw !== null && raw !== "0";
  })();

  // Drop only the funnel position and the previous run's output. Identity
  // (userId, email), the pet name and prior uploads are deliberately kept —
  // a returning visitor still gets their photo carousel and a pre-filled
  // email, so "start over" costs them one style tap, not a re-upload.
  function clearFunnelProgress() {
    saveState({ currentStep: "style", styleName: null, resultId: null });
  }

  /**
   * Strip ?fresh=1 once it has been applied. Without this, a mid-flow reload
   * or a bfcache restore would re-apply the reset and yank the user back to
   * the style picker — including out of an in-flight generation.
   */
  function consumeFreshParam() {
    if (!isFreshStart) return;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("fresh")) return;
      url.searchParams.delete("fresh");
      history.replaceState(history.state, "", url.toString());
    } catch (e) {}
  }

  // ── App state ───────────────────────────────────────────────────────

  const state = {
    userId: null,
    sessionId: null,
    file: null,
    photoBlobUrl: null,
    photoUrl: null, // public URL when the photo came from a carousel tile (no blob)
    uploadId: null,
    petName: "",
    styleName: null,
    email: "",
    phone: "",
    resultId: null,
    // Once true, late-arriving error handlers from earlier promises (e.g. a
    // delayed upload reject after generation already succeeded) MUST NOT
    // navigate the user backwards. Reset only when "Make another" is clicked.
    resultShown: false,
    // Generation kicks off in the background when the user leaves the name
    // step, so it runs in parallel with the email step. The email step does
    // NOT re-kick — it just awaits the in-flight job. resultId is set as
    // soon as /generate returns, so the status of the job is queryable
    // even if the in-memory promise is interrupted.
    generationStarted: false,
    generationPromise: null,
    // Prior-uploads carousel — populated once on boot. Empty list means we
    // never reveal the carousel and the upload step keeps the legacy dropzone.
    priorUploads: [],
    // Per-selection race protection. Every "new selection" event (file pick,
    // tile click, Replace) increments selectSeq and replaces `abort` with a
    // fresh AbortController. Late-arriving processAndUpload responses check
    // selectSeq before applying state. `uploadSource` distinguishes fresh
    // uploads (which we DELETE on supersede) from tile selections (which
    // point at confirmed photos that other surfaces / future sessions may
    // want to reuse).
    selectSeq: 0,
    abort: null,
    uploadSource: null, // 'fresh' | 'tile' | null
    // Phone country pill state. We capture the user-selected country (US/
    // CA/MX) for analytics — even though US and CA share the +1 dial code,
    // the country itself is useful signal.
    phoneCountry: "US",
    phoneDial: "+1",
  };

  // ── Section roots ───────────────────────────────────────────────────
  //
  // `root` itself is resolved at the top of the file — the Config block needs it.

  const tool = root.querySelector("[data-p2a-tool]");
  const viewModeRoot = root.querySelector("[data-p2a-view]");
  const hero = root.querySelector("[data-p2a-hero]");

  const stylesConfig = (() => {
    const node = root.querySelector("[data-p2a-styles]");
    if (!node) return [];
    try {
      return JSON.parse(node.textContent.trim());
    } catch (e) {
      console.error("[photo-to-art] failed to parse styles config", e);
      return [];
    }
  })();

  // Randomized style-display plan storage. Declared here (before the boot
  // call below) because bootToolMode → renderStyleGrid → getDisplayPlan runs
  // immediately, and these would otherwise be in the temporal dead zone.
  const DISPLAY_KEY = "instame_p2a_display" + KEY_SUFFIX;
  let _displayPlan = null;

  // ── Boot ────────────────────────────────────────────────────────────

  state.userId = getOrCreateUserId();
  state.sessionId = getOrCreateSessionId();

  // Note: `view` is a Shopify-reserved query param that switches to an
  // alternate template suffix — using anything else avoids that collision.
  const viewId = getQueryParam("share");
  if (viewId) {
    isShareView = true;
    bootViewMode(viewId);
  } else {
    bootToolMode();
  }

  // bfcache repair: when the page is restored from the back/forward cache
  // (Back from a product page — reliable in desktop device-emulation), scripts
  // do NOT re-run, so the dynamically-injected free-portrait tile is absent from
  // the restored snapshot until a hard refresh. Re-inject it (idempotent, and a
  // no-op outside the test variant / before a result exists) without re-rendering
  // the product tiles, so their personalized composites survive the restore.
  // Mirrors the pageshow handling in assets/cart-icon.js.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted || isShareView || !state.resultId) return;
    const container = document.querySelector(
      '[data-p2a-step="result"] [data-p2a-upsell]',
    );
    if (!container) return;
    const grid = container.querySelector("[data-p2a-upsell-grid]");
    if (grid) injectFreePortraitTile(grid, container);
  });

  // ── View mode ───────────────────────────────────────────────────────

  async function bootViewMode(resultId) {
    if (tool) tool.hidden = true;
    if (hero) hero.hidden = true;
    if (viewModeRoot) viewModeRoot.hidden = false;

    // Set on state so the shared share helpers (buildShareUrl, downloadResult)
    // work without needing parallel implementations.
    state.resultId = resultId;
    state.resultShown = true;

    try {
      const res = await fetch(
        `${API_BASE}/api/photo-to-art/result/${resultId}`,
      );
      if (!res.ok) throw new Error("Result not found");
      const data = await res.json();

      if (!data.image_ready) {
        $("[data-p2a-view-headline]").textContent =
          "This portrait is still being made — try again in a moment.";
        $("[data-p2a-view-subtitle]").textContent =
          "Refresh the page in a few seconds.";
        return;
      }

      // `?v=display` → the email-sized JPEG (~100-300 KB) instead of the
      // 5-10 MB master. This <img> is a pure display surface — and the share
      // links arriving from delivery emails were painfully slow on the
      // master. Download / native share / Pinterest below still fetch the
      // full-quality master.
      const imgUrl = `${API_BASE}/api/photo-to-art/image/${resultId}?v=display`;
      $("[data-p2a-view-img]").src = imgUrl;

      // Hydrate state so share buttons (buildShareCopy, downloadResult)
      // reference the actual style and pet from the shared result.
      state.petName = data.pet_name || null;
      state.styleName = data.ai_image_name || null;

      if (data.pet_name && data.ai_image_name) {
        $("[data-p2a-view-headline]").textContent =
          `Meet ${data.pet_name} — in ${labelForStyle(data.ai_image_name)}`;
      } else if (data.pet_name) {
        $("[data-p2a-view-headline]").textContent = `Meet ${data.pet_name}`;
      }

      bindViewShareButtons();

      // Share-link upsell shelf — view-mode variant. styleName comes from
      // the backend payload (data.ai_image_name); falls back to bestsellers
      // inside loadUpsell if it's missing.
      const viewUpsell = viewModeRoot
        ? viewModeRoot.querySelector("[data-p2a-upsell]")
        : null;
      if (viewUpsell) loadUpsell(viewUpsell, state.styleName);
    } catch (e) {
      console.error("[photo-to-art] view mode load failed", e);
      $("[data-p2a-view-headline]").textContent =
        "We couldn't load this portrait";
      $("[data-p2a-view-subtitle]").textContent =
        "The link may have expired. Make your own portrait below.";
    }
  }

  function bindViewShareButtons() {
    const downloadBtn = $("[data-p2a-view-download]");
    const copyLinkBtn = $("[data-p2a-view-copy-link]");
    const fbBtn = $("[data-p2a-view-share-fb]");
    const twBtn = $("[data-p2a-view-share-twitter]");
    const piBtn = $("[data-p2a-view-share-pinterest]");
    const rdBtn = $("[data-p2a-view-share-reddit]");

    if (downloadBtn) downloadBtn.addEventListener("click", downloadResult);

    if (copyLinkBtn) {
      copyLinkBtn.addEventListener("click", () => {
        copyShareLink($("[data-p2a-view-copy-link-label]"));
      });
    }

    if (fbBtn) {
      fbBtn.addEventListener("click", (e) => {
        e.currentTarget.setAttribute(
          "href",
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildShareUrl())}`,
        );
      });
    }

    if (twBtn) {
      twBtn.addEventListener("click", (e) => {
        e.currentTarget.setAttribute(
          "href",
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(buildShareCopy())}&url=${encodeURIComponent(buildShareUrl())}`,
        );
      });
    }

    if (piBtn) {
      piBtn.addEventListener("click", (e) => {
        // Full-quality master on purpose — this is the media Pinterest pins.
        const imgUrl = `${API_BASE}/api/photo-to-art/image/${state.resultId}`;
        e.currentTarget.setAttribute(
          "href",
          `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(buildShareUrl())}&media=${encodeURIComponent(imgUrl)}&description=${encodeURIComponent(buildShareCopy())}`,
        );
      });
    }

    if (rdBtn) {
      rdBtn.addEventListener("click", (e) => {
        e.currentTarget.setAttribute(
          "href",
          `https://www.reddit.com/submit?url=${encodeURIComponent(buildShareUrl())}&title=${encodeURIComponent(buildShareCopy())}`,
        );
      });
    }
  }

  // ── Tool mode ───────────────────────────────────────────────────────

  function bootToolMode() {
    // Defensive — earlier versions toggled body.style.overflow when the
    // photo-guide modal opened/closed. If a prior session crashed mid-
    // toggle, the body could be left non-scrollable. CSS now drives the
    // lock (body:has selector) but clear any inline carry-over too.
    if (document.body.style.overflow === "hidden") {
      document.body.style.overflow = "";
    }
    renderStyleGrid();
    bindUpload();
    bindNameStep();
    bindStyleStep();
    bindEmailStep();
    bindResultStep();
    bindLightbox();
    bindNextButtons();
    bindBackButtons();
    bindPhotoGuide();
    restoreFromStorage();
    // restoreFromStorage has applied the real is-active classes, so the
    // section's resume pre-activation tag (html.p2a-resume-<step>, set
    // before first paint to stop the style step flashing) can come off —
    // its CSS would otherwise hide any OTHER step the user navigates to.
    document.documentElement.classList.remove(
      "p2a-resume-upload",
      "p2a-resume-name",
      "p2a-resume-email"
    );
    initCarousel();
    autoFillIdentity();

    // Fire the GTM event for the initial step on fresh visits / saved-style
    // resumes. Resumed non-style steps already fired via goTo() inside
    // restoreFromStorage, so gate on style only to avoid double-firing.
    const activeStep = document.querySelector("[data-p2a-step].is-active");
    const initialStep =
      activeStep && activeStep.getAttribute("data-p2a-step");
    trackP2A("photo_to_art_page_loaded", {
      initial_step: initialStep || "style",
      is_fresh: !initialStep || initialStep === "style",
      // Distinct from is_fresh above (which just means "landed on the style
      // step"): this is specifically an arrival via a ?fresh=1 promo link,
      // so campaign traffic is separable from organic first visits.
      fresh_start: isFreshStart,
    });
    if (initialStep === "style") {
      syncStepUrlAndEvent("style");
    }
    consumeFreshParam();
  }

  /**
   * Photo guidelines modal — opened via the "Photo guidelines" toggle in
   * the upload step. Mirrors the product-wizard pattern (same image, same
   * tip list). Closes on close-button, Escape, or overlay click.
   */
  function bindPhotoGuide() {
    const modal = $("[data-p2a-photo-guide]");
    if (!modal) return;

    // Body scroll-lock is handled by CSS (body:has(.p2a__photo-guide:not([hidden]))),
    // so we only need to flip the modal's hidden attribute here.
    const open = () => {
      modal.hidden = false;
    };
    const close = () => {
      modal.hidden = true;
    };

    $$("[data-p2a-photo-guide-toggle]").forEach((btn) => {
      btn.addEventListener("click", open);
    });

    const closeBtn = modal.querySelector("[data-p2a-photo-guide-close]");
    if (closeBtn) closeBtn.addEventListener("click", close);

    // Click on overlay (but not inside the content card) closes.
    modal.addEventListener("click", (e) => {
      if (e.target === modal) close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !modal.hidden) close();
    });
  }

  /**
   * Pre-fill the email + phone inputs from the user's stored identity, so
   * returning users don't have to retype. Same UX promise as the prior-
   * uploads carousel — silent if we have nothing on file. Never overrides
   * a value the user has already typed (or that restoreFromStorage put
   * back from localStorage).
   */
  async function autoFillIdentity() {
    const emailInput = $("[data-p2a-email-input]");
    const phoneInput = $("[data-p2a-phone-input]");
    const submitBtn = $("[data-p2a-email-submit]");

    // Signed-in Shopify customers: their own session email wins outright
    // (frontend#98) — no backend lookup needed, still fully editable.
    const customerEmail = typeof window.__instameCustomerEmail === "string"
      ? window.__instameCustomerEmail.trim()
      : "";
    if (customerEmail && emailInput && !emailInput.value) {
      emailInput.value = customerEmail;
      state.email = customerEmail;
      saveState({ email: customerEmail });
      if (submitBtn) submitBtn.disabled = !isValidEmail(customerEmail);
    }

    if (!state.userId) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/users/get?user_id=${encodeURIComponent(state.userId)}`,
      );
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));

      // Only prefill values that parse as a REAL address/number: once
      // /api/users/get starts returning masked forms (backend#64), a
      // masked string must never end up submitted as the delivery email.
      if (data.email && !/[*]/.test(String(data.email)) && isValidEmail(data.email) && emailInput && !emailInput.value) {
        emailInput.value = data.email;
        state.email = data.email;
        saveState({ email: data.email });
        if (submitBtn) submitBtn.disabled = !isValidEmail(data.email);
      }
      if (data.phone && !/[*]/.test(String(data.phone)) && phoneInput && !phoneInput.value) {
        phoneInput.value = data.phone;
        state.phone = data.phone;
      }

      // Returning visitor whose saved address only came back masked: offer
      // a one-tap that submits the step with use_saved — the real address
      // resolves server-side and never reaches this browser. The masked
      // form is display-safe by construction.
      const useSavedBtn = $("[data-p2a-use-saved]");
      const masked = typeof data.email_masked === "string" ? data.email_masked : "";
      if (
        useSavedBtn &&
        emailInput &&
        !emailInput.value &&
        masked &&
        /[*]/.test(masked)
      ) {
        useSavedBtn.textContent = `Use my saved email (${masked})`;
        useSavedBtn.hidden = false;
      }
    } catch (e) {
      // Silent — auto-fill is a polish, never block the flow on it.
    }
  }

  // Stable per-user display plan: a shuffled style order + one chosen example
  // image per style. We randomize both to reduce the bias a user has toward a
  // style just because of its position or example photo (some users see
  // watercolor first / a cat example; others see pencil first / a dog). The
  // plan is persisted so reloads don't reshuffle — keeps the UX consistent and
  // gives each user a stable assignment for funnel analysis. Regenerated if the
  // curated style set changes (signature mismatch). DISPLAY_KEY and
  // _displayPlan are declared up near the boot call (TDZ — see comment there).
  function shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function styleExamples(s) {
    if (Array.isArray(s.examples) && s.examples.length) return s.examples;
    if (s.example) return [s.example];
    return [];
  }

  function getDisplayPlan() {
    if (_displayPlan) return _displayPlan;
    // Signature includes the example URLs (not just style names) so that
    // swapping the example images — even with the same styles — invalidates a
    // returning user's persisted plan and regenerates it. Without this, a
    // stale plan would keep pointing at old (possibly deleted) image URLs.
    const sig = stylesConfig
      .map((s) => `${s.name}:${styleExamples(s).join("|")}`)
      .sort()
      .join(",");
    try {
      const saved = JSON.parse(localStorage.getItem(DISPLAY_KEY) || "null");
      if (
        saved &&
        saved.sig === sig &&
        Array.isArray(saved.order) &&
        saved.examples
      ) {
        _displayPlan = saved;
        return saved;
      }
    } catch (e) {}

    const order = shuffle(stylesConfig.map((s) => s.name));
    const examples = {};
    stylesConfig.forEach((s) => {
      const imgs = styleExamples(s);
      examples[s.name] = imgs.length
        ? imgs[Math.floor(Math.random() * imgs.length)]
        : "";
    });
    _displayPlan = { sig, order, examples };
    try {
      localStorage.setItem(DISPLAY_KEY, JSON.stringify(_displayPlan));
    } catch (e) {}
    return _displayPlan;
  }

  function renderStyleGrid() {
    const grid = $("[data-p2a-styles-grid]");
    if (!grid) return;
    const plan = getDisplayPlan();
    const byName = new Map(stylesConfig.map((s) => [s.name, s]));
    const ordered = plan.order.map((n) => byName.get(n)).filter(Boolean);
    grid.innerHTML = ordered
      .map((s) => {
        const img = plan.examples[s.name] || styleExamples(s)[0] || "";
        // CDN transform params: the originals are print-size (2048x3072) but
        // render in ~200px cards. URLs carry ?v=, but guard the separator so a
        // future param-less URL still works.
        const sep = img.includes("?") ? "&" : "?";
        const imgSrc = img ? `${img}${sep}width=480` : "";
        const imgSrcset = img
          ? `${img}${sep}width=320 320w, ${img}${sep}width=640 640w, ${img}${sep}width=960 960w`
          : "";
        return `
        <button type="button" class="p2a__style-card" data-p2a-style="${s.name}">
          <span class="p2a__style-thumb">
            <img src="${imgSrc}" srcset="${imgSrcset}" sizes="(min-width: 720px) 33vw, 50vw" alt="${s.label} example" loading="lazy" width="320" height="480">
          </span>
          <span class="p2a__style-label">${s.label}</span>
        </button>`;
      })
      .join("");

    grid.addEventListener("click", (ev) => {
      const card = ev.target.closest("[data-p2a-style]");
      if (!card) return;
      state.styleName = card.getAttribute("data-p2a-style");
      saveState({ styleName: state.styleName });
      showError("style", null);
      trackP2A("photo_to_art_style_card_clicked", { style: state.styleName });
      // Most users pick exactly one style, so a separate Continue click is just
      // friction (the Continue button has been removed). Auto-advance to upload.
      // advanceFrom("style") fires photo_to_art_style_continued and navigates.
      advanceFrom("style");
    });
  }

  // ── Upload step ─────────────────────────────────────────────────────

  function bindUpload() {
    const dropzone = $("[data-p2a-dropzone]");
    const fileInput = $("[data-p2a-file-input]");
    const empty = $("[data-p2a-dropzone-empty]");
    const previewWrap = $("[data-p2a-dropzone-preview]");
    const previewImg = $("[data-p2a-preview-img]");
    const replaceBtn = $("[data-p2a-replace]");

    fileInput.addEventListener("change", (ev) => {
      const file = ev.target.files && ev.target.files[0];
      // Clear before handleFile so re-picking the same file still fires
      // "change" next time (browsers skip the event when the value is
      // unchanged). The captured File stays valid after the reset.
      fileInput.value = "";
      if (file) handleFile(file);
    });

    // The empty placeholder is a button — tap to open the file picker.
    // The preview area is intentionally NOT clickable: tapping the
    // uploaded photo on mobile must not re-open the file picker, since
    // that swallows scroll gestures and traps the user mid-page.
    empty.addEventListener("click", () => {
      fileInput.value = "";
      fileInput.click();
    });

    // Drag-and-drop on the whole dropzone (works for both empty and
    // preview states; only triggers a new upload, never opens picker).
    ["dragenter", "dragover"].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.add("is-dragging");
      });
    });
    ["dragleave", "drop"].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove("is-dragging");
      });
    });
    dropzone.addEventListener("drop", (e) => {
      const file =
        e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });

    replaceBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Bump seq + abort any in-flight processing so a late-arriving response
      // from the prior pick can't write into the now-empty slot.
      state.selectSeq += 1;
      if (state.abort) state.abort.abort();
      state.abort = null;
      // If we're abandoning a fresh upload, hard-delete it from the backend
      // so it doesn't haunt the carousel on the next page load.
      if (state.uploadSource === "fresh" && state.uploadId) {
        window.InstaMePhotoUpload.deleteUpload(state.uploadId, {
          userId: state.userId,
        });
        removePriorUpload(state.uploadId);
      }
      fileInput.value = "";
      state.file = null;
      state.uploadId = null;
      state.photoUrl = null;
      state.uploadSource = null;
      saveState({ uploadId: null, photoUrl: null });
      if (state.photoBlobUrl) URL.revokeObjectURL(state.photoBlobUrl);
      state.photoBlobUrl = null;
      previewWrap.hidden = true;
      // If the user has prior uploads, surface them again so they can pick a
      // different existing photo or upload a brand-new one. Otherwise fall
      // back to the legacy single-tile auto-open of the file picker.
      if (state.priorUploads && state.priorUploads.length > 0) {
        revealCarousel();
        empty.hidden = true;
      } else {
        empty.hidden = false;
        fileInput.click();
      }
    });

    // The chosen photo is shown on the name step (the photo step auto-advances
    // past the upload preview where the remove "X" lives). Mirror that
    // affordance — tapping this X returns to the photo step, which surfaces
    // the carousel so the user can re-select or upload a different photo.
    const namePhotoRemove = $("[data-p2a-name-photo-remove]");
    if (namePhotoRemove) {
      namePhotoRemove.addEventListener("click", (e) => {
        e.preventDefault();
        goTo("upload");
      });
    }

    async function handleFile(file) {
      showError("upload", null);

      // Per-selection race protection: bump selectSeq, abort any in-flight
      // processing/upload from the previous selection. Capture the new seq
      // and abort signal locally so the late-response guard below can detect
      // supersession even if state.selectSeq has moved on again.
      state.selectSeq += 1;
      const mySeq = state.selectSeq;
      if (state.abort) state.abort.abort();
      state.abort = new AbortController();
      const myAbort = state.abort;

      // If we're abandoning a fresh upload (one we made this session),
      // hard-delete it from the backend. Tile selections aren't deleted —
      // they point at confirmed photos that should persist.
      if (state.uploadSource === "fresh" && state.uploadId) {
        window.InstaMePhotoUpload.deleteUpload(state.uploadId, {
          userId: state.userId,
        });
        removePriorUpload(state.uploadId);
      }

      // Reset slot state — will be re-populated after upload completes.
      state.uploadId = null;
      state.photoUrl = null;
      state.file = null;
      state.uploadSource = null;
      if (state.photoBlobUrl) URL.revokeObjectURL(state.photoBlobUrl);
      state.photoBlobUrl = null;

      const carousel = $("[data-p2a-carousel]");
      const inCarouselMode = carousel && !carousel.hidden;

      if (inCarouselMode) {
        setPlusTileLoading(true);
      } else {
        // Show "Processing photo..." placeholder while the core snapshots
        // and (if needed) converts HEIC. Avoids briefly showing a broken
        // image preview on desktop browsers that can't render HEIC.
        previewWrap.hidden = true;
        empty.hidden = false;
        const cta = empty.querySelector(".p2a__dropzone-cta");
        if (cta) {
          empty.dataset.originalCta = empty.dataset.originalCta || cta.textContent;
          cta.textContent = "Processing photo…";
        }
      }

      // Showing the preview only after the upload lands is intentional — it
      // signals "upload succeeded" so the user never sees a preview followed
      // by a "couldn't upload" error, and there's no in-between state where
      // a Replace click would try to DELETE an upload_id we don't have yet.
      const uploadStartedAt = Date.now();
      const fileExt = (file.name.match(/\.(\w+)$/i) || [])[1]?.toLowerCase() || "unknown";
      const fileMime = file.type || "unknown";
      const originalSizeBytes = file.size || 0;
      trackP2A("photo_to_art_upload_started", {
        format: fileMime,
        extension: fileExt,
        original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
        style: state.styleName || null,
      });

      let result;
      try {
        result = await window.InstaMePhotoUpload.processAndUpload(file, {
          userId: state.userId,
          extraFields: {
            source: SOURCE,
            pet_name: state.petName || undefined,
          },
          signal: myAbort.signal,
        });
      } catch (err) {
        if (err && err.code === "ABORTED") {
          // Aborted by a tile pick or Replace (both null state.abort) —
          // clear the + tile spinner, or its aria-busy guard blocks the
          // file picker forever. A superseding handleFile owns a fresh
          // controller and clears its own spinner.
          if (!state.abort) {
            setPlusTileLoading(false);
          }
          return;
        }
        if (state.selectSeq !== mySeq) return; // superseded — silently drop
        console.error(
          "[photo-to-art] upload failed:",
          (err && err.code) || err.name,
          (err && err.technical) || err.message,
          err,
        );
        trackP2A("photo_to_art_upload_failed", {
          error_code: (err && err.code) || "UNKNOWN",
          message: (err && err.technical) || err.message,
          format: fileMime,
          extension: fileExt,
          original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
          retryable: !!(err && err.retryable),
          duration_ms: Date.now() - uploadStartedAt,
        });
        setPlusTileLoading(false);
        const cta = empty.querySelector(".p2a__dropzone-cta");
        if (cta && empty.dataset.originalCta) cta.textContent = empty.dataset.originalCta;
        showError(
          "upload",
          (err && err.userMessage) || err.message || "Couldn't upload your photo.",
        );
        return;
      }

      // Late-response guard — user picked another file (or hit Replace)
      // while this upload was in flight. Drop the result; the newer
      // selection's response will be the source of truth.
      if (state.selectSeq !== mySeq) return;

      trackP2A("photo_to_art_upload_succeeded", {
        format: fileMime,
        extension: fileExt,
        detected_mime: result.detectedMime || null,
        was_converted: !!result.wasConverted,
        was_compressed: !!result.wasCompressed,
        original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
        final_size_mb: +((result.finalSize || 0) / 1024 / 1024).toFixed(2),
        duration_ms: Date.now() - uploadStartedAt,
        style: state.styleName || null,
      });

      // Apply state
      state.uploadId = result.upload_id;
      state.photoUrl = result.photo_url;
      state.file = result.processedFile;
      state.photoBlobUrl = URL.createObjectURL(result.processedFile);
      state.uploadSource = "fresh";
      saveState({ uploadId: state.uploadId, photoUrl: state.photoUrl });

      // Insert tile + mark selected (carousel mode only). Fresh uploads
      // also need to populate the priorUploads array so the Replace flow
      // can later remove them from the carousel.
      if (inCarouselMode) {
        setPlusTileLoading(false);
        const fresh = {
          upload_id: state.uploadId,
          photo_url: state.photoUrl,
          photo_path: null,
          pet_name: "",
          created_at: new Date().toISOString(),
        };
        state.priorUploads = [fresh, ...state.priorUploads];
        insertFreshTile(fresh);
        updateTileSelection(state.uploadId);
        hideCarousel();
      } else {
        const cta = empty.querySelector(".p2a__dropzone-cta");
        if (cta && empty.dataset.originalCta) cta.textContent = empty.dataset.originalCta;
      }
      previewImg.src = state.photoBlobUrl;
      previewWrap.hidden = false;
      empty.hidden = true;
      // Once the upload lands, jump straight to the name step — the photo
      // step has no Continue button. uploadId was set above.
      advanceFrom("upload");
    }
  }

  /** Remove a tile from the carousel DOM and the in-memory priorUploads
   *  array. Called after firing DELETE for an abandoned fresh upload so
   *  the tile doesn't linger as a phantom. */
  function removePriorUpload(uploadId) {
    if (!uploadId) return;
    state.priorUploads = state.priorUploads.filter((u) => u.upload_id !== uploadId);
    document
      .querySelectorAll(`[data-p2a-tile][data-upload-id="${uploadId}"]`)
      .forEach((t) => t.remove());
  }

  // ── Prior-Uploads Carousel ──────────────────────────────────────────
  //
  // Mirrors the product wizard's carousel pattern: if the user has already
  // uploaded photos (under this anon user_id), we offer those tiles as
  // shortcuts rather than forcing a fresh upload. Selecting a tile reuses
  // the existing user_uploads row and pre-fills the pet name on the name
  // step. Brand-new uploads still go through handleFile + /api/photos/upload.

  async function initCarousel() {
    const carousel = $("[data-p2a-carousel]");
    if (!carousel) return;

    let uploads = [];
    try {
      uploads = await fetchPriorUploads();
    } catch (e) {
      console.warn("[photo-to-art] fetchPriorUploads failed", e);
    }
    if (!uploads || uploads.length === 0) return;

    state.priorUploads = uploads;

    if (maybeRevealRestoredCarousel()) return;

    // If we already have a chosen photo (fresh blob upload mid-flow, or a
    // restored selection), keep that view. The carousel will surface the
    // next time the user hits "Replace".
    const previewWrap = $("[data-p2a-dropzone-preview]");
    const previewVisible = previewWrap && !previewWrap.hidden;
    if (previewVisible || state.uploadId || state.file) return;

    revealCarousel();
  }

  /**
   * A reload restored onto the photo step re-shows the locked preview, but
   * the photo step has no Continue button, and goTo("upload")'s carousel
   * reveal ran before priorUploads loaded — stranding the user. Mirror that
   * reveal once the prior-uploads fetch has landed: carousel up, restored
   * photo highlighted, so tapping any tile re-advances. Called from
   * initCarousel. Idempotent.
   */
  function maybeRevealRestoredCarousel() {
    if (!state.uploadId) return false;
    if (!state.priorUploads || !state.priorUploads.length) return false;
    const activeStep = $("[data-p2a-step].is-active");
    if (!activeStep || activeStep.getAttribute("data-p2a-step") !== "upload") {
      return false;
    }
    const previewWrap = $("[data-p2a-dropzone-preview]");
    if (previewWrap) previewWrap.hidden = true;
    revealCarousel();
    updateTileSelection(state.uploadId);
    return true;
  }

  async function fetchPriorUploads() {
    const url = `${API_BASE}/api/photos/list?user_id=${encodeURIComponent(state.userId)}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data.uploads) ? data.uploads : [];
  }

  function revealCarousel() {
    const carousel = $("[data-p2a-carousel]");
    const dropzone = $("[data-p2a-dropzone]");
    const empty = $("[data-p2a-dropzone-empty]");
    if (!carousel) return;

    renderCarousel();
    carousel.hidden = false;
    // Hide the dropzone label entirely while the carousel is the active
    // entry point — the + tile inside the carousel takes its role. The
    // dropzone is unhidden on tile selection (to host the preview) or on
    // Replace-without-priors.
    if (dropzone) dropzone.hidden = true;
    if (empty) empty.hidden = true;
    bindCarouselEvents();
  }

  function hideCarousel() {
    const carousel = $("[data-p2a-carousel]");
    const dropzone = $("[data-p2a-dropzone]");
    if (carousel) carousel.hidden = true;
    if (dropzone) dropzone.hidden = false;
  }

  function renderCarousel() {
    const carousel = $("[data-p2a-carousel]");
    if (!carousel) return;

    const tiles = state.priorUploads.map((u) => buildTileHTML(u)).join("");
    carousel.innerHTML = `
      <div class="p2a__carousel-scroll" data-p2a-carousel-scroll>
        <button type="button" class="p2a__tile p2a__tile--plus" data-p2a-tile-plus aria-label="Upload new photo">
          <svg class="p2a__tile-plus-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span class="p2a__tile-spinner"></span>
        </button>
        ${tiles}
      </div>
    `;

    // Mark the currently-restored tile as selected (when restoring from storage).
    if (state.uploadId) updateTileSelection(state.uploadId);
  }

  function buildTileHTML(u) {
    const photoUrl = String(u.photo_url || "").replace(/"/g, "&quot;");
    const name = String(u.pet_name || "").replace(/"/g, "&quot;");
    const id = String(u.upload_id || "").replace(/"/g, "&quot;");
    return `
      <button type="button" class="p2a__tile" data-p2a-tile
              data-upload-id="${id}"
              data-photo-url="${photoUrl}"
              data-pet-name="${name}"
              draggable="false"
              aria-label="Select pet photo">
        <img src="${photoUrl}" alt="" loading="lazy" width="120" height="120" draggable="false">
      </button>
    `;
  }

  function bindCarouselEvents() {
    const carousel = $("[data-p2a-carousel]");
    const fileInput = $("[data-p2a-file-input]");
    if (!carousel) return;

    // The + tile and scroller are destroyed by every renderCarousel()
    // innerHTML rebuild — rebind their per-element handlers on every call
    // (the old elements are gone, so nothing double-binds).
    const plusTile = carousel.querySelector("[data-p2a-tile-plus]");

    plusTile?.addEventListener("dragover", (e) => {
      e.preventDefault();
      plusTile.classList.add("p2a__tile--dragover");
    });
    plusTile?.addEventListener("dragleave", () => {
      plusTile.classList.remove("p2a__tile--dragover");
    });
    plusTile?.addEventListener("drop", (e) => {
      e.preventDefault();
      plusTile.classList.remove("p2a__tile--dragover");
      const file =
        e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) {
        // Reuse the bindUpload handleFile path. The simplest way is to set
        // the file input and fire a change event — but for clarity we set
        // state directly and reuse the preview render.
        forwardDroppedFileToInput(file);
      }
    });

    const scroller = carousel.querySelector("[data-p2a-carousel-scroll]");
    if (scroller) bindCarouselDragScroll(scroller, carousel);

    // The delegated click handler lives on the persistent carousel element —
    // bind it once; it survives every re-render.
    if (carousel._clickBound) return;
    carousel._clickBound = true;

    // Tile selection + the + tile (event delegation — tiles and the + tile
    // are re-rendered). Suppressed when the click is the tail of a
    // drag-to-scroll gesture.
    carousel.addEventListener("click", (e) => {
      if (carousel._suppressNextClick) {
        carousel._suppressNextClick = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const plus = e.target.closest("[data-p2a-tile-plus]");
      if (plus) {
        if (plus.getAttribute("aria-busy") === "true") {
          return;
        }
        if (fileInput) {
          fileInput.value = "";
          fileInput.click();
        }
        return;
      }
      const tile = e.target.closest("[data-p2a-tile]:not(.p2a__tile--plus)");
      if (!tile) return;
      selectTile(tile);
    });
  }

  function bindCarouselDragScroll(scroller, carousel) {
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = 0;
    const DRAG_THRESHOLD = 5;

    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      e.preventDefault();
      scroller.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      scroller.classList.remove("p2a__carousel-scroll--dragging");
      document.body.classList.remove("p2a-carousel-dragging");
      if (moved > DRAG_THRESHOLD) carousel._suppressNextClick = true;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    scroller.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      isDown = true;
      moved = 0;
      startX = e.clientX;
      startScrollLeft = scroller.scrollLeft;
      scroller.classList.add("p2a__carousel-scroll--dragging");
      document.body.classList.add("p2a-carousel-dragging");
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    });

    scroller.addEventListener("dragstart", (e) => e.preventDefault());
  }

  function forwardDroppedFileToInput(file) {
    // The bindUpload handler is wired to the file input's change event.
    // Use DataTransfer to populate fileInput.files, then dispatch change.
    const fileInput = $("[data-p2a-file-input]");
    if (!fileInput) return;
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    } catch (e) {
      console.warn("[photo-to-art] dropped-file forward failed", e);
    }
  }

  function selectTile(tile) {
    const uploadId = tile.getAttribute("data-upload-id");
    const photoUrl = tile.getAttribute("data-photo-url");
    const petName = tile.getAttribute("data-pet-name") || "";

    if (!uploadId || !photoUrl) return;

    // Bump seq + abort any in-flight processing from a previous fresh pick.
    state.selectSeq += 1;
    if (state.abort) state.abort.abort();
    state.abort = null;

    // If we're switching away from a fresh upload, hard-delete it. Skip if
    // the user clicked the same tile (its upload_id == our fresh upload_id),
    // which can happen because handleFile auto-selects fresh tiles.
    if (
      state.uploadSource === "fresh" &&
      state.uploadId &&
      state.uploadId !== uploadId
    ) {
      window.InstaMePhotoUpload.deleteUpload(state.uploadId, {
        userId: state.userId,
      });
      removePriorUpload(state.uploadId);
    }

    if (state.photoBlobUrl) URL.revokeObjectURL(state.photoBlobUrl);
    state.photoBlobUrl = null;
    state.file = null;
    state.uploadSource = "tile";

    state.uploadId = uploadId;
    state.photoUrl = photoUrl;
    // Always overwrite the in-memory pet name (matches product wizard tile-pick).
    state.petName = petName;

    saveState({ uploadId, photoUrl, petName });

    // Pre-fill the name input so when the user lands on the name step it's ready.
    // The input event refreshes the Continue button's disabled state.
    const nameInput = $("[data-p2a-name-input]");
    if (nameInput) {
      nameInput.value = petName;
      nameInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    // Show the standard preview state (replaces carousel + empty placeholder).
    const previewWrap = $("[data-p2a-dropzone-preview]");
    const previewImg = $("[data-p2a-preview-img]");
    const empty = $("[data-p2a-dropzone-empty]");

    if (previewImg) previewImg.src = photoUrl;
    if (previewWrap) previewWrap.hidden = false;
    if (empty) empty.hidden = true;
    hideCarousel();

    showError("upload", null);
    updateTileSelection(uploadId);

    trackP2A("photo_to_art_tile_selected", {
      had_pet_name: !!petName,
      style: state.styleName || null,
    });

    // Selecting a tile auto-advances to the name step (no Continue button).
    // uploadId is set above.
    advanceFrom("upload");
  }

  function updateTileSelection(uploadId) {
    const carousel = $("[data-p2a-carousel]");
    if (!carousel) return;
    carousel.querySelectorAll("[data-p2a-tile]").forEach((t) => {
      t.classList.toggle(
        "p2a__tile--selected",
        t.getAttribute("data-upload-id") === uploadId,
      );
    });
  }

  /** Toggle the busy spinner inside the + tile (during a fresh upload). */
  function setPlusTileLoading(loading) {
    const plusTile = $("[data-p2a-tile-plus]");
    if (!plusTile) return;
    plusTile.setAttribute("aria-busy", loading ? "true" : "false");
  }

  /**
   * Insert a freshly-uploaded photo as the first tile in the carousel
   * (right after the + tile). Mirrors the product wizard's pattern so
   * the user sees their new upload immediately without a full re-render.
   */
  function insertFreshTile(upload) {
    const scroll = $("[data-p2a-carousel-scroll]");
    if (!scroll) return;
    const plusTile = scroll.querySelector("[data-p2a-tile-plus]");
    const wrapper = document.createElement("div");
    wrapper.innerHTML = buildTileHTML(upload).trim();
    const newTile = wrapper.firstElementChild;
    if (plusTile && plusTile.nextSibling) {
      scroll.insertBefore(newTile, plusTile.nextSibling);
    } else if (plusTile) {
      scroll.appendChild(newTile);
    }
  }

  // ── Name step ───────────────────────────────────────────────────────

  function bindNameStep() {
    const input = $("[data-p2a-name-input]");
    const continueBtn = document.querySelector(
      "[data-p2a-step='name'] [data-p2a-next-from='name']",
    );

    const refreshContinueState = () => {
      if (!continueBtn) return;
      const value = (input.value || "").trim();
      continueBtn.disabled = value.length === 0;
    };

    input.addEventListener("input", () => {
      refreshContinueState();
      showError("name", null);
    });

    // Enter key submits the step (only when valid).
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (continueBtn && !continueBtn.disabled) continueBtn.click();
      }
    });

    refreshContinueState();
  }

  function bindStyleStep() {
    // selection handled in renderStyleGrid; this step's continue button is
    // enabled there once a style is picked.
  }

  // ── Email step ──────────────────────────────────────────────────────

  function bindEmailStep() {
    const form = $("[data-p2a-email-form]");
    const emailInput = $("[data-p2a-email-input]");
    const phoneInput = $("[data-p2a-phone-input]");
    const submitBtn = $("[data-p2a-email-submit]");

    bindPhoneCountry();
    // Strip non-digits as the user types — the dial code lives on the
    // country pill, so the input only ever holds the local number.
    phoneInput.addEventListener("input", () => {
      const cleaned = (phoneInput.value || "").replace(/\D/g, "");
      if (cleaned !== phoneInput.value) phoneInput.value = cleaned;
    });

    // Submit button stays disabled until the email passes a basic format
    // check (non-empty local part + @ + non-empty domain with a dot).
    const refreshSubmitState = () => {
      submitBtn.disabled = !isValidEmail((emailInput.value || "").trim());
    };

    // "Did you mean …?" typo correction. Suggest-only — never blocks
    // submit and never auto-replaces, so a rare-but-real domain is safe.
    // We only compute once a plausible TLD has been typed (@ + dot + 2+
    // chars) so we don't flash a guess mid-domain.
    const suggestEl = $("[data-p2a-email-suggest]");
    const suggestBtn = $("[data-p2a-email-suggest-btn]");
    let emailSuggestion = null;
    // Hard requirement before we touch any suggestion code: both elements
    // present AND the shared helper loaded. If an older cached lead-capture.js
    // or section markup is ever served, this is false and the email step runs
    // exactly as it did before the feature existed — the hint just no-ops. The
    // hint must never be able to break the core funnel.
    const canSuggest =
      !!suggestEl &&
      !!suggestBtn &&
      !!window.LeadCapture &&
      typeof window.LeadCapture.suggestEmail === "function";
    const looksComplete = (v) => /@[^@\s]+\.[^@\s]{2,}$/.test(v);
    const refreshSuggestion = () => {
      if (!canSuggest) return;
      try {
        const val = (emailInput.value || "").trim();
        const s = looksComplete(val)
          ? window.LeadCapture.suggestEmail(val)
          : null;
        if (s && s.toLowerCase() !== val.toLowerCase()) {
          if (emailSuggestion !== s) {
            trackP2A("photo_to_art_email_suggestion_shown", { suggestion: s });
          }
          emailSuggestion = s;
          suggestBtn.textContent = s;
          suggestEl.hidden = false;
        } else {
          emailSuggestion = null;
          suggestEl.hidden = true;
        }
      } catch (e) {
        // A bad suggestion can never be worth interrupting the email step.
        emailSuggestion = null;
        if (suggestEl) suggestEl.hidden = true;
      }
    };
    if (canSuggest) {
      suggestBtn.addEventListener("click", () => {
        if (!emailSuggestion) return;
        emailInput.value = emailSuggestion;
        saveState({ email: emailSuggestion });
        trackP2A("photo_to_art_email_suggestion_accepted", {
          suggestion: emailSuggestion,
        });
        emailSuggestion = null;
        suggestEl.hidden = true;
        refreshSubmitState();
        emailInput.focus();
      });
    }

    emailInput.addEventListener("input", () => {
      refreshSubmitState();
      refreshSuggestion();
      saveState({ email: (emailInput.value || "").trim() });
    });
    emailInput.addEventListener("blur", refreshSuggestion);
    refreshSubmitState();

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitEmail();
    });

    // One-tap saved-email path: tapping IS the continue action. Disabled
    // immediately so a double-tap can't run the submit flow twice.
    const useSavedBtn = $("[data-p2a-use-saved]");
    if (useSavedBtn) {
      useSavedBtn.addEventListener("click", () => {
        useSavedBtn.disabled = true;
        useSavedBtn.hidden = true;
        submitEmail(true);
      });
    }

    async function submitEmail(useSaved) {
      const usingSaved = useSaved === true;
      // use_saved: the address stays server-side; every email-specific step
      // below (validation, suggestion, identify) is skipped.
      const email = usingSaved ? "" : (emailInput.value || "").trim();
      // Local digits → full E.164 by prepending the selected country's
      // dial code. Empty input means the (optional) phone wasn't given.
      // A typed/pasted leading country code ("+1 555…" → 11 digits) is
      // stripped first so the NSN validator can accept it.
      const localDigits = normalizeNsn(
        (phoneInput.value || "").replace(/\D/g, ""),
        state.phoneCountry,
      );
      let phone = localDigits ? `${state.phoneDial}${localDigits}` : "";

      if (!usingSaved && !isValidEmail(email)) {
        showError(
          "email",
          "Please enter a valid email so we can send your art.",
        );
        trackP2A("photo_to_art_error", {
          step: "email",
          reason: "invalid_email",
        });
        emailInput.focus();
        return;
      }
      // Phone is optional, so a number that still fails validation after
      // normalization must not block the email capture or art delivery.
      // Klaviyo drops profiles whose phone_number isn't valid E.164, so the
      // bad number is dropped and the submit continues with email only.
      let phoneDropped = false;
      if (localDigits && !isValidPhone(localDigits, state.phoneCountry)) {
        phone = "";
        phoneDropped = true;
        trackP2A("photo_to_art_error", {
          step: "email",
          reason: "invalid_phone_dropped",
          country: state.phoneCountry,
        });
      }
      showError("email", null);
      if (phoneDropped) {
        showNotice(
          "email",
          "We couldn't verify that phone number, so we saved your email only.",
        );
      }

      // User pushed past a "did you mean …?" hint — worth knowing how often
      // the suggestion was right vs an overzealous guess on a real domain.
      if (!usingSaved && emailSuggestion && emailSuggestion.toLowerCase() !== email.toLowerCase()) {
        trackP2A("photo_to_art_email_suggestion_ignored", {
          suggestion: emailSuggestion,
          submitted: email,
        });
      }
      if (suggestEl) suggestEl.hidden = true;

      if (!usingSaved) {
        state.email = email;
      }
      state.phone = phone;

      trackP2A("photo_to_art_email_continued", {
        has_phone: !!phone,
        country: state.phoneCountry,
        used_saved: usingSaved,
      });

      // Stamp Klaviyo + welcome discount. Note: the photo-to-art delivery
      // email no longer depends on this — see attachEmail below.
      // No raw address exists client-side in use_saved mode, and the profile
      // was identified + subscribed when the address was first captured — so
      // identify is skipped (a typed phone is forfeited in this rare path).
      if (!usingSaved && typeof window.instameIdentify === "function") {
        // subscribeEmail/subscribeSms turn the profile into an actual marketing
        // subscriber — identify alone only creates a NEVER_SUBSCRIBED profile.
        // Consent is collected via the disclosure copy under the "Make My Art"
        // button. Phone is optional, so SMS only fires when one was entered.
        window.instameIdentify(email, phone || null, {
          source: SOURCE,
          customSource: "Photo to Art Tool",
          subscribeEmail: true,
          subscribeSms: !!phone,
        });
      }

      // Make sure a generation is in flight before we await attach-email.
      // Normally this is a no-op because the name step already kicked
      // one off. The retry path (previous attempt failed and cleared the
      // gate) needs this so attach-email's wait-for-resultId loop has a
      // resultId to find — otherwise it sits for 30s before timing out.
      maybeStartGeneration();

      // Hand the email off to the backend job row. The backend's send
      // pipeline reads delivery_email from the row, never users.email —
      // this is the single source of truth for "where to send THIS
      // portrait", and it removes the race where instameIdentify hadn't
      // landed yet when generation finished. If the job is already
      // completed (cache/dedup hit) the backend sends right here.
      //
      // Also disable the back buttons during the await — attachDeliveryEmail
      // can sit for up to 30s waiting for state.resultId, and a back-press
      // during that window would invalidate the very generation we're about
      // to await on, dropping the user into a "Something went wrong" error.
      const backBtns = $$("[data-p2a-back-from='email']");
      submitBtn.disabled = true;
      backBtns.forEach((b) => {
        b.disabled = true;
      });
      try {
        await attachDeliveryEmail(usingSaved ? null : email, phone, usingSaved);
      } catch (e) {
        console.warn("[photo-to-art] attach-email failed", e);
        trackP2A("photo_to_art_attach_email_failed", {
          result_id: state.resultId || null,
          message: (e && e.message) || String(e),
        });
        // Keep going — if the row update actually landed despite the
        // network error, the generation tail still picks up the email.
      } finally {
        submitBtn.disabled = false;
        backBtns.forEach((b) => {
          b.disabled = false;
        });
      }

      goTo("generating");
      awaitGenerationAndShow();
    }

    async function attachDeliveryEmail(email, phone, useSaved) {
      if (!state.userId || !state.resultId) {
        // No result_id yet means generation hasn't been registered — the
        // generate call is in flight from the name step. Wait briefly
        // for it to land so we can attach the email to the right row.
        const start = Date.now();
        while (!state.resultId && Date.now() - start < 30_000) {
          await new Promise((r) => setTimeout(r, 200));
        }
        if (!state.resultId) throw new Error("No result_id to attach to");
      }
      const res = await fetch(`${API_BASE}/api/photo-to-art/attach-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: state.userId,
          result_id: state.resultId,
          ...(useSaved === true ? { use_saved: true } : { email }),
          ...(phone ? { phone } : {}),
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`attach-email failed: ${res.status} ${text}`);
      }
    }
  }

  /**
   * Country pill + dropdown for the phone field. Limited to US/CA/MX for
   * now — those share +1 (US/CA) or +52 (MX). The user-selected country
   * is captured even when its dial code matches another, since the
   * country itself is useful analytics signal.
   */
  function bindPhoneCountry() {
    const wrap = $("[data-p2a-phone-wrap]");
    const trigger = $("[data-p2a-phone-country]");
    const menu = $("[data-p2a-phone-menu]");
    const flagEl = $("[data-p2a-phone-flag]");
    const dialEl = $("[data-p2a-phone-dial]");
    if (!wrap || !trigger || !menu || !flagEl || !dialEl) return;

    const setOpen = (open) => {
      menu.hidden = !open;
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
    };

    const selectOption = (option) => {
      const country = option.getAttribute("data-country");
      const dial = option.getAttribute("data-dial");
      const flag = option.getAttribute("data-flag");
      if (!country || !dial || !flag) return;
      state.phoneCountry = country;
      state.phoneDial = dial;
      flagEl.textContent = flag;
      dialEl.textContent = dial;
      // Reflect selection in the menu for accessibility.
      $$("[data-p2a-phone-option]").forEach((opt) => {
        opt.setAttribute("aria-selected", opt === option ? "true" : "false");
      });
      setOpen(false);
    };

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      setOpen(menu.hidden);
    });

    menu.addEventListener("click", (e) => {
      const opt = e.target.closest("[data-p2a-phone-option]");
      if (!opt) return;
      selectOption(opt);
    });

    // Click outside closes the menu.
    document.addEventListener("click", (e) => {
      if (menu.hidden) return;
      if (!wrap.contains(e.target)) setOpen(false);
    });

    document.addEventListener("keydown", (e) => {
      if (!menu.hidden && e.key === "Escape") {
        setOpen(false);
        trigger.focus();
      }
    });

    // Mark the default (US) as selected for screen readers.
    const defaultOption = menu.querySelector(
      `[data-p2a-phone-option][data-country="${state.phoneCountry}"]`,
    );
    if (defaultOption) defaultOption.setAttribute("aria-selected", "true");
  }

  // ── Result step ─────────────────────────────────────────────────────

  function bindResultStep() {
    // GRADUATED (exp_p2a_result_grid). The losing arm's Download / Share /
    // Copy Link buttons and social icon row were bound here. They lived in the
    // block this experiment hid, so they are gone and so are their listeners.
    // downloadResult / shareResultNative / copyShareLink all survive — the grid
    // tile calls them directly, which is why they were never proxied.
    const onRestart = () => {
      // Capture style/result before clearState/reset null them out below.
      trackP2A("photo_to_art_make_another_clicked", { style: state.styleName, result_id: state.resultId });
      clearState();
      // Reset state minus userId/sessionId
      state.file = null;
      state.uploadId = null;
      state.photoUrl = null;
      state.petName = "";
      state.styleName = null;
      state.resultId = null;
      state.resultShown = false;
      state.generationStarted = false;
      state.generationPromise = null;
      if (state.photoBlobUrl) URL.revokeObjectURL(state.photoBlobUrl);
      state.photoBlobUrl = null;

      $("[data-p2a-file-input]").value = "";
      $("[data-p2a-name-input]").value = "";
      $("[data-p2a-dropzone-preview]").hidden = true;
      $("[data-p2a-dropzone-empty]").hidden = false;

      // The user just produced a portrait — that upload is now in their
      // priors list. Refresh the carousel so the next round shows it as a
      // selectable tile. This handles the first-time-user case too: on
      // initial visit they had no priors (carousel hidden), but after their
      // first generation we should reveal it for the next round.
      hideCarousel();
      fetchPriorUploads()
        .then((u) => {
          if (Array.isArray(u) && u.length > 0) {
            state.priorUploads = u;
            revealCarousel();
            $("[data-p2a-dropzone-empty]").hidden = true;
          }
        })
        .catch(() => {});

      goTo("style");
    };
    // Both restart controls share this handler: control's bottom-of-page button
    // and the test variant's "Make another style" link under the shelf heading.
    $$("[data-p2a-restart]").forEach((b) => b.addEventListener("click", onRestart));
  }

  function buildShareUrl() {
    if (!state.resultId) return location.href;
    const base = `${location.origin}${location.pathname}`;
    return `${base}?share=${state.resultId}`;
  }

  // ── Shared share helpers (used by result step + view mode) ──────────

  async function downloadResult() {
    if (!state.resultId) return;
    hideDownloadFailed();
    // `?download=1` → the backend answers the same bytes with
    // Content-Disposition: attachment, and the query string is a distinct
    // HTTP-cache key — so this never reuses a cache entry poisoned by the
    // result <img> load (stored without Access-Control-Allow-Origin before
    // Vary: Origin shipped, and immutable for a year).
    const url = `${API_BASE}/api/photo-to-art/image/${state.resultId}?download=1`;

    // Touch devices only: prefer the native share sheet with a files-only
    // payload — iOS's "Save Image" is the sole route into the Photos app,
    // and Android's sheet offers gallery apps. Desktop Safari and Chrome
    // ALSO pass canShare({files}), but a desktop Download should land in
    // ~/Downloads, so desktop always takes the plain anchor download below.
    if ((isIOS() || isAndroid()) && navigator.canShare) {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`image fetch failed: ${res.status}`);
        }
        const blob = await res.blob();
        // Name/type follow the served bytes (watermarked masters are JPEG,
        // clean ones PNG) — hardcoding .png would mislabel the file.
        const type = (res.headers.get("Content-Type") || "image/png")
          .split(";")[0]
          .trim();
        const ext =
          type === "image/jpeg" ? "jpg" : type === "image/webp" ? "webp" : "png";
        const file = new File(
          [blob],
          `lilaland-${state.styleName || "portrait"}-${state.resultId}.${ext}`,
          { type },
        );
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file] });
            trackP2A("photo_to_art_downloaded", { style: state.styleName });
            return;
          } catch (e) {
            if (e && e.name === "AbortError") {
              // The sheet opened and the user dismissed it — a download
              // attempt genuinely started, so it still counts.
              trackP2A("photo_to_art_downloaded", { style: state.styleName });
              return;
            }
            // Real failure (e.g. in-app browser blocks share) — fall through.
          }
        }
      } catch (e) {
        // fetch / file build failed — fall through to the anchor download
      }
    }

    // Anchor navigation to the attachment variant. The server's
    // Content-Disposition header is what makes this a download — browsers
    // ignore the `download` attribute cross-origin (kept only for the
    // same-origin dev gateway). No fetch/blob → immune to CORS failures and
    // poisoned caches; no window.open → immune to popup blockers.
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = `lilaland-${state.styleName || "portrait"}-${state.resultId}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      trackP2A("photo_to_art_downloaded", { style: state.styleName });
    } catch (e) {
      trackP2A("photo_to_art_download_failed", {
        error_name: (e && e.name) || "UNKNOWN",
        style: state.styleName,
      });
      showDownloadFailed();
      return;
    }

    // We can't observe whether the OS actually saved the file (in-app
    // browsers fail silently), so on iOS surface the long-press hint as a
    // backup once we've reached this fallback.
    if (isIOS()) showLongPressHint();
  }

  // Native share of the portrait (image file if supported, else URL). Used by
  // BOTH the control Share button and the test tile's Share button so the two
  // run identical code — no proxy.
  async function shareResultNative() {
    const shareUrl = buildShareUrl();
    const shareData = {
      title: "LilaLand pet portrait",
      text: buildShareCopy(),
      url: shareUrl,
    };
    // Prepare the image file first (better preview on iOS). No share sheet is
    // shown during preparation, so this part is safe to fall back from. Only
    // the file PREP is retryable: once navigator.share() opens the sheet we must
    // NOT call it again — a cancelled/failed share rejects the promise, and
    // re-sharing would open a SECOND sheet (the iOS "Share opens twice / have to
    // close it twice" bug). So resolve the payload, then share exactly once.
    let payload = shareData;
    if (navigator.canShare && state.resultId) {
      try {
        // Full-quality master on purpose — the shared file is the user's
        // free digital copy (never the compressed `?v=display` derivative).
        const res = await fetch(
          `${API_BASE}/api/photo-to-art/image/${state.resultId}`,
        );
        const blob = await res.blob();
        const file = new File([blob], `pet-portrait.png`, {
          type: "image/png",
        });
        if (navigator.canShare({ files: [file] })) {
          payload = { ...shareData, files: [file] };
        }
      } catch (e) {
        // couldn't prepare the file — share the URL only
      }
    }
    try {
      await navigator.share(payload);
      trackP2A("photo_to_art_shared", {
        platform: "native",
        style: state.styleName,
        with_image: payload !== shareData,
      });
    } catch (e) {
      // user cancelled or share failed — silent (do NOT re-open the sheet)
    }
  }

  // The download-failed message exists once per surface (result step + view
  // mode, colocated with the long-press hint so it's visible in every
  // result-layout arm); toggle all instances — hidden surfaces don't render.
  function showDownloadFailed() {
    document.querySelectorAll("[data-p2a-download-error]").forEach((el) => {
      el.hidden = false;
    });
  }

  function hideDownloadFailed() {
    document.querySelectorAll("[data-p2a-download-error]").forEach((el) => {
      el.hidden = true;
    });
  }

  function showLongPressHint() {
    document.querySelectorAll("[data-p2a-long-press-hint]").forEach((el) => {
      const wasHidden = el.hidden;
      el.hidden = false;
      if (!wasHidden) {
        el.classList.remove("p2a__share-tip--wiggle");
        // Force reflow so removing + re-adding the class restarts the animation.
        void el.offsetWidth;
        el.classList.add("p2a__share-tip--wiggle");
      }
    });
  }

  async function copyShareLink(labelEl) {
    const shareUrl = buildShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      if (labelEl) {
        const original = labelEl.textContent;
        labelEl.textContent = "Copied!";
        setTimeout(() => {
          labelEl.textContent = original;
        }, 1800);
      }
    } catch (e) {
      prompt("Copy this link:", shareUrl);
    }
  }

  // ── Step transitions ────────────────────────────────────────────────

  function bindNextButtons() {
    $$("[data-p2a-next-from]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const from = btn.getAttribute("data-p2a-next-from");
        const skip = btn.getAttribute("data-skip") === "1";
        advanceFrom(from, { skip });
      });
    });
  }

  function bindBackButtons() {
    $$("[data-p2a-back-from]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const from = btn.getAttribute("data-p2a-back-from");
        if (from) goBack(from);
      });
    });
  }

  /**
   * Navigate to the previous step. Always invalidates any in-flight
   * generation so the next forward press re-kicks /generate with the
   * (possibly changed) current inputs. /generate is dedup-idempotent on
   * the backend — if the user didn't actually change anything, the call
   * returns the same result_id, so this is free in the no-op case.
   */
  function goBack(currentStep) {
    invalidateGeneration();
    trackP2A("photo_to_art_back", { from: currentStep });
    const idx = STEPS.indexOf(currentStep);
    if (idx <= 0) return;
    const prev = STEPS[idx - 1];
    goTo(prev);
  }

  function advanceFrom(step, opts) {
    opts = opts || {};

    if (step === "style") {
      if (!state.styleName) {
        showError("style", "Pick a style to continue.");
        return;
      }
      // Capture WHERE the chosen style was shown (position in the randomized
      // order) and WHICH example image the user saw — lets us check that the
      // randomization actually flattened the position/example bias.
      const plan = getDisplayPlan();
      trackP2A("photo_to_art_style_continued", {
        style: state.styleName,
        position: plan.order ? plan.order.indexOf(state.styleName) : null,
        example: (plan.examples && plan.examples[state.styleName]) || null,
      });
      goTo("upload");
      return;
    }

    if (step === "upload") {
      // Upload happens immediately on file pick (handleFile) or is reused
      // from a tile selection — both set state.uploadId before calling
      // advanceFrom. Defensive guard for the (impossible in normal flow)
      // case where we somehow have no upload_id yet.
      if (!state.uploadId) {
        showError("upload", "Please pick a photo first.");
        return;
      }
      trackP2A("photo_to_art_photo_continued", {
        from_carousel: state.uploadSource === "tile",
      });
      goTo("name");
      return;
    }

    if (step === "name") {
      const value = ($("[data-p2a-name-input]").value || "").trim();
      if (!value) {
        showError("name", "Please enter your pet's name to continue.");
        $("[data-p2a-name-input]").focus();
        return;
      }
      state.petName = value;
      saveState({ petName: state.petName });
      trackP2A("photo_to_art_name_continued", {
        pet_name_length: value.length,
      });
      // Kick off generation in the background so it runs in parallel with
      // the email step. runGeneration internally waits for uploadId.
      maybeStartGeneration();
      goTo("email");
      return;
    }
  }

  // ── Generation orchestration ────────────────────────────────────────

  // Clear the in-flight generation so the next forward press re-kicks
  // /generate with current inputs. Any still-running runGeneration from
  // before will eventually return and write its (possibly stale) resultId
  // — that's fine in the common case where inputs didn't change (backend
  // dedup returns the same id), and acceptable in the rare cross-input
  // race where the user changed inputs and the old response lands last;
  // worst case the user sees the old art and clicks "Make Another."
  function invalidateGeneration() {
    state.resultId = null;
    state.generationStarted = false;
    state.generationPromise = null;
    saveState({ resultId: null });
  }

  function maybeStartGeneration() {
    if (state.generationStarted) return;
    state.generationStarted = true;
    state.generationStartedAt = Date.now();
    console.log("[photo-to-art] kicking off generation", {
      styleName: state.styleName,
      petName: state.petName || null,
      uploadId: state.uploadId,
    });
    state.generationPromise = runGeneration();
    // Swallow unhandled rejection — awaitGenerationAndShow will re-await
    // and surface the error in the email step's UI. Also drop the
    // started gate so the next click can re-kick a fresh attempt
    // without forcing a page reload — the cached rejected promise is
    // still on state.generationPromise so the email-step await still
    // sees the real error.
    state.generationPromise.catch(() => {
      state.generationStarted = false;
    });
  }

  async function awaitGenerationAndShow() {
    if (!state.generationPromise) {
      // Safety net — name step always starts the job, but if it didn't
      // (logic bug), surface a friendly error rather than hanging.
      console.error("[photo-to-art] no generation in flight at email step");
      goTo("email");
      showError("email", "Something went wrong. Please try again.");
      return;
    }
    try {
      await state.generationPromise;
      trackP2A("photo_to_art_generation_succeeded", {
        style: state.styleName,
        duration_ms: state.generationStartedAt
          ? Date.now() - state.generationStartedAt
          : null,
      });
      showResult();
    } catch (e) {
      console.error("[photo-to-art] generation failed", e);
      // If the result already showed (stale promise rejected after success),
      // keep the user on the result step.
      if (state.resultShown) return;
      trackP2A("photo_to_art_generation_failed", {
        style: state.styleName,
        error: (e && e.message) || String(e),
        phase: (e && e.phase) || "unknown",
        status: (e && e.status) || null,
        duration_ms: state.generationStartedAt
          ? Date.now() - state.generationStartedAt
          : null,
      });
      // Clear the failed job + gates so the next click on Make my art
      // re-kicks a fresh /generate via maybeStartGeneration in
      // submitEmail — without this the user is trapped on the email
      // step (currentStep is persisted, there's no back button).
      state.resultId = null;
      state.generationStarted = false;
      state.generationPromise = null;
      saveState({ resultId: null });
      goTo("email");
      // Surface the actual error string so screen recordings + console
      // logs make the failure cause obvious. The error.message comes from
      // runGeneration: it's either "generate failed: <status> <body>",
      // the row-level error_message from /status, "Photo upload didn't
      // complete in time", or "Timed out waiting for art".
      // A 429 is OUR per-IP generate ceiling — shared by everyone on one network,
      // which at an event is the whole venue behind a single NAT. It needs its own
      // message because the general one below appends "ensure the image you
      // uploaded is a pet", which on this failure is false and actively harmful:
      // it blames the customer's photo for our capacity and sends them to a staff
      // member about a limit that clears by itself.
      //
      // Keyed on the STATUS, never on the message text. The backend can reword its
      // error at any time, and a text match would stop matching SILENTLY —
      // restoring exactly the misattribution this removes. The pet-photo sentence
      // is suppressed here rather than deleted, because it is presumably useful
      // advice on a genuine generation failure.
      if (e && e.status === 429) {
        const wait = e.retryAfterMinutes;
        showError(
          "email",
          wait
            ? `Lots of people are making art right now — this limit is shared by everyone here, so it isn't your photo or your phone. Try again in about ${wait} minute${wait === 1 ? "" : "s"}. Your photo is saved.`
            : "Lots of people are making art right now — this limit is shared by everyone here, so it isn't your photo or your phone. Give it a few minutes and try again. Your photo is saved.",
        );
        return;
      }
      const detail = (e && e.message) || "Please try again.";
      showError(
        "email",
        `Couldn't make your art: ${detail}. Please ensure the image you uploaded is a pet — that's a common cause for the error.`,
      );
    }
  }

  /**
   * Re-attach to an already-known generation job without re-calling
   * /generate. Used on restore when we have a stored resultId — the
   * previous in-flight Promise died with the page, so we recreate the
   * polling tail.
   */
  async function resumeGeneration(resultId) {
    state.resultId = resultId;
    const final = await pollResult(resultId);
    if (final.status === "completed") return;
    const err = new Error(final.error_message || "Generation failed");
    err.phase = final.error_message === "Timed out waiting for art"
      ? "resume_polling_timeout"
      : "resume_polling_returned_failed";
    throw err;
  }

  /**
   * Look up the current state of a generation job. Tries once, retries
   * once after 3 seconds on 404 (handles the brief window where the row
   * isn't visible yet just after /generate). Returns one of:
   *   { state: "completed" }
   *   { state: "generating" }
   *   { state: "failed", message }
   *   { state: "missing" }   // 404 after retry
   *   { state: "error" }     // network or 5xx
   */
  async function fetchJobState(resultId) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, STATE_LOOKUP_RETRY_MS));
      }
      let res;
      try {
        res = await fetch(
          `${API_BASE}/api/photo-to-art/status?result_id=${encodeURIComponent(resultId)}`,
        );
      } catch (e) {
        return { state: "error" };
      }
      if (res.status === 404) continue;
      if (!res.ok) return { state: "error" };
      let data;
      try {
        data = await res.json();
      } catch (e) {
        return { state: "error" };
      }
      if (data.status === "completed") return { state: "completed" };
      if (data.status === "failed")
        return { state: "failed", message: data.error_message };
      if (data.status === "generating") return { state: "generating" };
      return { state: "error" };
    }
    return { state: "missing" };
  }

  /**
   * Restore flow when we have a stored resultId. Asks the backend for the
   * job's current state, then routes:
   *   completed         → show result immediately
   *   generating        → switch to generating step + poll until done
   *   missing/failed/error → drop the stored id, kick a fresh generation
   */
  async function resumeFromStoredJob() {
    const result = await fetchJobState(state.resultId);
    // The user may have clicked "Make another" while we were awaiting
    // the status call. If state was reset, bail out.
    if (state.resultId == null) return;

    if (result.state === "completed") {
      showResult();
      return;
    }

    if (result.state === "generating") {
      state.generationStarted = true;
      state.generationPromise = resumeGeneration(state.resultId);
      state.generationPromise.catch(() => {});
      goTo("generating");
      awaitGenerationAndShow();
      return;
    }

    // missing, failed, or error — drop the stale id and start fresh.
    state.resultId = null;
    saveState({ resultId: null });
    state.generationStarted = false;
    state.generationPromise = null;
    if (state.uploadId && state.styleName) {
      maybeStartGeneration();
      goTo("generating");
      awaitGenerationAndShow();
    } else {
      goTo("email");
    }
  }

  /**
   * Hydrate the in-memory `state` from localStorage and resume the wizard
   * at the saved step. Called at the end of bootToolMode, after all
   * binders have run and the DOM is ready.
   *
   * Resume table:
   *   style    → mark card selected, stay on step
   *   upload   → render preview from photoUrl, stay
   *   name     → pre-fill name input, stay
   *   email    → pre-fill email; re-attach to in-flight generation if any
   *   generating → resume polling (or re-derive via /generate dedup), then goTo
   *   result   → re-show via showResult()
   */
  function restoreFromStorage() {
    const saved = loadState();
    if (!saved) return;

    // ?fresh=1 — rewind the saved funnel position before anything reads it,
    // then fall through so the photo / name / email hydration below still
    // runs. The step routing at the bottom sees "style" and returns early.
    if (isFreshStart) {
      saved.currentStep = "style";
      saved.styleName = null;
      saved.resultId = null;
      clearFunnelProgress();
    }

    // Hydrate in-memory state
    if (saved.styleName) state.styleName = saved.styleName;
    if (saved.uploadId) state.uploadId = saved.uploadId;
    // photoUrl must come back too — goTo("name") reads it to populate the
    // name-step photo (photoBlobUrl never survives a reload).
    if (saved.photoUrl) state.photoUrl = saved.photoUrl;
    if (saved.petName) state.petName = saved.petName;
    if (saved.email) state.email = saved.email;
    if (saved.resultId) state.resultId = saved.resultId;

    // Drop tampered styleName values that aren't in our curated list, so
    // the querySelector below can't be coerced into a malformed selector.
    if (
      state.styleName &&
      !stylesConfig.some((s) => s.name === state.styleName)
    ) {
      state.styleName = null;
    }

    // Style needs no on-load visual: state.styleName is already restored above,
    // and selecting a card auto-advances (no Continue button, no persistent
    // selected highlight — the green border is hover-only).

    // Upload preview — restore from photoUrl. onerror handles a stale URL
    // (e.g. row deleted in Supabase) by clearing the saved upload and
    // bumping the user back to the upload step.
    //
    // Skipped when the resume lands ON the photo step: there the restored
    // UI is the carousel (maybeRevealRestoredCarousel), so painting the
    // preview first would flash a big photo that collapses into the tile
    // row moments later.
    // Also skipped on a ?fresh=1 start: there the user re-enters the photo
    // step forward, through the style picker. If the prior-uploads fetch came
    // back empty the carousel never reveals, and a painted locked preview
    // would strand them — the photo step has no Continue button.
    const skipPreviewPaint = saved.currentStep === "upload" || isFreshStart;
    if (saved.photoUrl && state.uploadId && !skipPreviewPaint) {
      const previewImg = $("[data-p2a-preview-img]");
      const previewWrap = $("[data-p2a-dropzone-preview]");
      const empty = $("[data-p2a-dropzone-empty]");
      if (previewImg && previewWrap && empty) {
        previewImg.onerror = () => {
          state.uploadId = null;
          state.photoUrl = null;
          saveState({ uploadId: null, photoUrl: null });
          previewWrap.hidden = true;
          empty.hidden = false;
          goTo("upload");
        };
        previewImg.src = saved.photoUrl;
        previewWrap.hidden = false;
        empty.hidden = true;
      }
    }

    // Name — pre-fill input. Dispatch input so the name step's
    // Continue button picks up the restored value and enables itself.
    if (state.petName) {
      const nameInput = $("[data-p2a-name-input]");
      if (nameInput) {
        nameInput.value = state.petName;
        nameInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }

    // Email — pre-fill input + recompute submit button enabled state
    if (state.email) {
      const emailInput = $("[data-p2a-email-input]");
      const submitBtn = $("[data-p2a-email-submit]");
      if (emailInput) {
        emailInput.value = state.email;
        if (submitBtn) submitBtn.disabled = !isValidEmail(state.email);
      }
    }

    // Resume per step
    const targetStep = saved.currentStep;
    if (!targetStep || targetStep === "style") return;

    if (targetStep === "upload" || targetStep === "name") {
      goTo(targetStep);
      return;
    }

    if (targetStep === "email") {
      // Generation kicked off when user left the name step. Re-attach.
      // If we have a resultId, just poll. Otherwise re-call /generate
      // (its tuple-dedup will return any existing job's resultId).
      if (state.resultId) {
        state.generationStarted = true;
        state.generationPromise = resumeGeneration(state.resultId);
        state.generationPromise.catch(() => {});
      } else if (state.uploadId && state.styleName) {
        maybeStartGeneration();
      }
      goTo("email");
      return;
    }

    if (targetStep === "generating") {
      if (!state.uploadId || !state.styleName) {
        // Insufficient data to resume — fall back to email step so the
        // user can retry. Shouldn't happen in practice.
        goTo("email");
        return;
      }
      if (state.resultId) {
        // Verify the stored job exists and route on its real state.
        resumeFromStoredJob();
        return;
      }
      // No resultId — kick a fresh generation. /generate is dedup-idempotent
      // by tuple, so this is safe even if a job was already in flight.
      state.generationStarted = true;
      state.generationPromise = runGeneration();
      state.generationPromise.catch(() => {});
      goTo("generating");
      awaitGenerationAndShow();
      return;
    }

    if (targetStep === "result") {
      if (state.resultId) {
        // Don't trust currentStep — verify the job actually completed
        // before showing. If the page died mid-generation but the step
        // got persisted, this routes us back to polling instead of
        // showing a broken image.
        resumeFromStoredJob();
        return;
      }
      // No resultId on result step is a recoverable inconsistency:
      // bump back to the email step so the user can retry.
      goTo("email");
      return;
    }
  }

  // Mirror the active step into the URL (?step=<slug>) and push a matching
  // event to the GTM dataLayer. Pure cosmetic — saved state is the source of
  // truth for which step is active, so a stale pasted URL gets corrected here
  // on the first goTo() during boot. Skipped in share-view mode (?share=…).
  function syncStepUrlAndEvent(step) {
    if (isShareView) return;
    const slug = STEP_SLUGS[step];
    if (!slug) return;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("step") !== slug) {
        url.searchParams.set("step", slug);
        history.replaceState(history.state, "", url.toString());
      }
    } catch (e) {}

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign(
        { event: `p2a_${slug.replace(/-/g, "_")}` },
        window.instameExperiments || {}
      ));
    } catch (e) {}
  }

  function goTo(step) {
    saveState({ currentStep: step });
    syncStepUrlAndEvent(step);
    $$("[data-p2a-step]").forEach((el) => {
      el.classList.toggle(
        "is-active",
        el.getAttribute("data-p2a-step") === step,
      );
    });
    $$("[data-p2a-progress-step]").forEach((el) => {
      const target = el.getAttribute("data-p2a-progress-step");
      const idx = STEPS.indexOf(target);
      const cur = STEPS.indexOf(step);
      el.classList.toggle("is-active", target === step);
      el.classList.toggle("is-done", idx >= 0 && idx < cur);
    });

    // Returning to the photo step with a photo already chosen (e.g. Back from
    // the name step) would strand the user — there's no Continue button. Show
    // the carousel with the current photo highlighted instead of the locked
    // preview, so tapping any tile re-advances.
    if (
      step === "upload" &&
      state.uploadId &&
      state.priorUploads &&
      state.priorUploads.length
    ) {
      const previewWrap = $("[data-p2a-dropzone-preview]");
      if (previewWrap) previewWrap.hidden = true;
      revealCarousel();
      updateTileSelection(state.uploadId);
    }

    // Populate the name-step photo with the currently-selected pet photo.
    // has-photo guards against an empty src showing a broken image.
    if (step === "name") {
      const namePhoto = $("[data-p2a-name-photo]");
      const namePhotoWrap = $("[data-p2a-name-photo-wrap]");
      const photo = state.photoBlobUrl || state.photoUrl;
      if (namePhoto && photo) {
        namePhoto.src = photo;
        if (namePhotoWrap) namePhotoWrap.classList.add("has-photo");
      } else if (namePhotoWrap) {
        namePhotoWrap.classList.remove("has-photo");
      }
    }

    if (step === "generating") rotateLoadingFacts();

    // Trigger the upsell shelf once we know the style (generating + result
    // both have a mount point). loadUpsell caches the response keyed on
    // styleName so we only fetch once.
    if (step === "generating" || step === "result") {
      const upsell = root.querySelector(
        `[data-p2a-step="${step}"] [data-p2a-upsell]`,
      );
      if (upsell) loadUpsell(upsell, state.styleName);
    }

    // Scroll the user back to the top of the tool wrapper on every step
    // change. Without this, advancing on a long step (style picker) leaves
    // the next step rendered below the fold.
    requestAnimationFrame(() => {
      try {
        if (tool) tool.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (e) {}
    });
  }

  let factTimer = null;
  function rotateLoadingFacts() {
    const node = $("[data-p2a-loading-fact]");
    if (!node) return;
    let i = 0;
    if (factTimer) clearInterval(factTimer);
    node.textContent = LOADING_FACTS[0];
    factTimer = setInterval(() => {
      i = (i + 1) % LOADING_FACTS.length;
      node.textContent = LOADING_FACTS[i];
    }, 4000);
  }

  // ── Backend calls ───────────────────────────────────────────────────
  //
  // The photo upload itself is no longer here — handleFile kicks
  // window.InstaMePhotoUpload.processAndUpload immediately on every file
  // pick, and the result lands on `state` before the user can click
  // Continue. The previous deferred-upload + _uploadInFlight singleton
  // pattern is gone.

  /**
   * Pure async pipeline — no UI side effects. Resolves when the result image
   * is ready (state.resultId is set), rejects on failure. The caller
   * (awaitGenerationAndShow) handles step transitions + error display so
   * this can safely run in the background while the user completes other
   * steps.
   */
  async function runGeneration() {
    // Wait for the upload (kicked off when leaving the upload step) to land.
    const start = Date.now();
    while (!state.uploadId && Date.now() - start < 30_000) {
      await new Promise((r) => setTimeout(r, 250));
    }
    if (!state.uploadId) {
      const err = new Error("Photo upload didn't complete in time");
      err.phase = "upload_wait_timeout";
      throw err;
    }

    // Update upload row with the pet name if we have one. Best-effort.
    if (state.petName) {
      try {
        await fetch(`${API_BASE}/api/photos/update-name`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: state.userId,
            upload_id: state.uploadId,
            pet_name: state.petName,
          }),
        });
      } catch (e) {
        // best-effort — generation will still work, name just won't be on the row
      }
    }

    // email may be null here when generation kicked off before the email
    // step. instameIdentify (called in submitEmail) associates email with
    // user_id on the backend so result delivery still works.
    let startRes;
    try {
      startRes = await fetch(`${API_BASE}/api/photo-to-art/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: state.userId,
          session_id: state.sessionId,
          user_upload_id: state.uploadId,
          ai_image_name: state.styleName,
          pet_name: state.petName || null,
          email: state.email || null,
        }),
      });
    } catch (e) {
      const err = new Error(`generate request threw: ${e && e.message}`);
      err.phase = "generate_network_error";
      throw err;
    }
    if (!startRes.ok) {
      const text = await startRes.text().catch(() => "");
      const err = new Error(`generate failed: ${startRes.status} ${text}`);
      err.phase = "generate_request_failed";
      err.status = startRes.status;
      // Exposed cross-origin by the API for exactly this, so a rate-limited
      // customer can be told the real wait rather than a guess.
      err.retryAfterMinutes = retryAfterMinutes(startRes.headers.get("Retry-After"));
      throw err;
    }
    const startData = await startRes.json();
    state.resultId = startData.result_id;
    saveState({ resultId: state.resultId });

    if (startData.status === "completed") return;

    const final = await pollResult(state.resultId);
    if (final.status === "completed") return;
    const err = new Error(final.error_message || "Generation failed");
    err.phase = final.error_message === "Timed out waiting for art"
      ? "polling_timeout"
      : "polling_returned_failed";
    throw err;
  }

  /**
   * Retry-After (seconds) -> whole minutes, rounded up. Null when the header is
   * absent, non-numeric or non-positive, which selects the vague copy — "in
   * about NaN minutes" is worse in front of a customer than naming no number.
   *
   * DUPLICATED, deliberately: assets/instant-preview.js carries an identical
   * copy. These are standalone theme assets with no import between them, and
   * moving five lines into the shared utilities.js days before an event is a
   * wider blast radius than the duplication costs. If you change one, change
   * the other — both surfaces describe the SAME per-IP ceiling to customers,
   * and they must not start disagreeing about the wait. Consolidation is
   * tracked as post-event work.
   */
  function retryAfterMinutes(headerValue) {
    const seconds = Number(headerValue);
    if (!Number.isFinite(seconds) || seconds <= 0) return null;
    return Math.max(1, Math.ceil(seconds / 60));
  }

  async function pollResult(resultId) {
    const start = Date.now();
    while (Date.now() - start < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const res = await fetch(
        `${API_BASE}/api/photo-to-art/status?result_id=${encodeURIComponent(resultId)}`,
      );
      if (!res.ok) continue;
      const data = await res.json();
      if (data.status === "completed" || data.status === "failed") return data;
    }
    return { status: "failed", error_message: "Timed out waiting for art" };
  }

  function showResult() {
    if (factTimer) {
      clearInterval(factTimer);
      factTimer = null;
    }
    state.resultShown = true;
    // `?v=display` → the email-sized JPEG (~100-300 KB) instead of the
    // 5-10 MB master — the reveal below waits for this download, so the
    // master made "Painting your masterpiece" linger far longer than needed.
    // Download / native share / Pinterest still fetch the full-quality
    // master; the free-portrait tile reuses this exact URL so it renders
    // straight from cache.
    const imgUrl = `${API_BASE}/api/photo-to-art/image/${state.resultId}?v=display`;
    const img = $("[data-p2a-result-img]");

    // Transition to the result step only after the image is fully loaded —
    // this keeps "Painting your masterpiece" visible during the final download
    // so the user never sees a blank placeholder between steps.
    const reveal = () => {
      img.onload = img.onerror = null;
      // Fall back to a generic word when the style is unknown — this happens when
      // a returning user restores a completed result whose style has since been
      // removed from the config (restoreFromStorage nulls styleName to block
      // regenerating it). The image still shows; only the caption degrades.
      // The result caption was removed from the markup; set it only if present
      // (kept null-safe so a future re-add just works).
      const nameEl = $("[data-p2a-result-name]");
      if (nameEl) {
        const styleLabel = labelForStyle(state.styleName) || "portrait";
        nameEl.textContent = state.petName
          ? `A ${styleLabel} of ${state.petName}.`
          : `A ${styleLabel}.`;
      }
      goTo("result");

      // Fires once the result image is loaded and the step is shown, for both
      // fresh generations and the resume/restore path, so restore-path viewers
      // still enter the funnel.
      trackP2A("photo_to_art_result_shown", { style: state.styleName, result_id: state.resultId });

      // Tell the tile personalizer the design just finished so it can upgrade the
      // upsell tiles from plain → personalized. The clean (no-watermark) image —
      // what tiles composite with — may land a moment after this watermarked
      // result (background removal runs last for "-opaque" styles), so the
      // personalizer retry-polls /api/user/styles for it.
      try {
        document.dispatchEvent(
          new CustomEvent("p2a:design-ready", {
            detail: {
              styleName: state.styleName || null,
              petName: state.petName || null,
              uploadId: state.uploadId || null,
            },
          }),
        );
      } catch (e) {}
    };

    img.onload = img.onerror = reveal;
    img.src = imgUrl;
    // If the browser already has the image cached (e.g. same result_id on
    // resume), onload won't fire — call reveal directly.
    if (img.complete && img.naturalWidth > 0) {
      img.onload = img.onerror = null;
      reveal();
    }
  }

  function labelForStyle(name) {
    const s = stylesConfig.find((x) => x.name === name);
    return s ? s.label : name;
  }

  // ── Upsell shelf ─────────────────────────────────────────────────────
  // Fetches a deduped grid of style-matched + bestseller products from
  // Shopify's Section Rendering API. The container element passes itself
  // in so we can have separate mount points for the wizard result step
  // and the share-link view mode without aliasing.

  function getStyleToCollectionMap() {
    const el = document.querySelector(
      "script[data-p2a-style-to-collection]",
    );
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  // Cache the fetched HTML so the generating step + result step + share
  // view don't double-fetch. Keyed by collection handle so re-picking a
  // different style still triggers a fresh fetch.
  const _upsellCache = new Map();
  const _upsellInFlight = new Map();

  // GTM conversion event for the P2A upsell shelf. Marketing watches
  // `p2a_upsell_product_click` as the funnel-bottom signal that the free
  // portrait actually pulled the visitor onto a real product page. The
  // listener lives on the (stable) container so it survives the innerHTML
  // swap that loadUpsell does when style changes.
  function attachUpsellClickTracking(container, styleName, collectionHandle) {
    container.dataset.p2aUpsellStyle = styleName || "";
    container.dataset.p2aUpsellCollection = collectionHandle || "";

    if (container.dataset.p2aUpsellTracked === "1") return;
    container.dataset.p2aUpsellTracked = "1";

    container.addEventListener("click", (e) => {
      // .product-card__link is an absolute overlay <a> whose children are
      // only a visually-hidden span — the image/title live in a sibling div.
      // closest(".product-card__link") therefore returns null for clicks on
      // the card body. Mirror tile-personalizer: match any <a href> ancestor
      // and filter to product URLs instead.
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (!/\/products\//.test(href)) return;

      // Resolve the <product-card> specifically. .card-gallery (a descendant)
      // also carries data-product-id, so a bare closest("[data-product-id]")
      // stops there — and the personalization flags (tilePersonalized,
      // tileUploadId, tilePetName) are stamped on the <product-card>, one level
      // up. Fall back to any data-product-id ancestor for non-product-card cards.
      const card =
        link.closest("product-card[data-product-id]") ||
        link.closest("[data-product-id]");
      const productId = card ? card.getAttribute("data-product-id") : null;
      const handleMatch = href.match(/\/products\/([^/?#]+)/);
      const productHandle = handleMatch ? handleMatch[1] : null;

      // The clicked card may be showing an image generated earlier for a
      // DIFFERENT pet than the one used this session (styles are keyed by style,
      // not pet). When the card is personalized, tile-personalizer stamps the
      // shown pet's upload id + name on it — prefer those so the product page's
      // pre-selected photo + name match the picture we send. Fall back to the
      // current session's pet for non-personalized (default-image) cards.
      const personalized = !!(card && card.dataset.tilePersonalized);
      const prefillUploadId = personalized
        ? card.dataset.tileUploadId || null
        : state.uploadId || null;
      const prefillPetName = personalized
        ? card.dataset.tilePetName || ""
        : state.petName || "";

      // Carry the pet photo + name to the product page so the wizard can
      // pre-select the photo and pre-fill the name. One-shot: product-wizard
      // consumes (and removes) this on the next page load, so it only applies to
      // this upsell → product navigation, not later direct visits.
      try {
        if (prefillUploadId || prefillPetName) {
          sessionStorage.setItem(
            "instame_p2a_prefill",
            JSON.stringify({
              uploadId: prefillUploadId,
              petName: prefillPetName,
              savedAt: Date.now(),
            }),
          );
        }
      } catch (_) {}

      // If the clicked card was showing a personalized composite (the pet on
      // this product), carry that exact image so the product page can show it
      // in the gallery instead of the default. tile-personalizer sets the
      // composite as a data: URL on the card image; plain cards have a normal
      // CDN src and store nothing, so those product pages keep their default.
      try {
        if (personalized && productHandle) {
          const cardImg =
            card.querySelector(".card-gallery img") ||
            card.querySelector(".product-card__image img") ||
            card.querySelector("img");
          const src = cardImg && (cardImg.currentSrc || cardImg.src);
          if (src && src.startsWith("data:")) {
            sessionStorage.setItem(
              "instame_p2a_card_image",
              JSON.stringify({
                handle: productHandle,
                productId: productId,
                src: src,
                savedAt: Date.now(),
              }),
            );
          }
        }
      } catch (_) {}

      const clickTarget = link.classList.contains("product-card__shop-btn")
        ? "shop_now_button"
        : "product_card";

      // Source = which surface the click came from, so marketing can
      // split conversion by funnel position (waiting screen vs post-art
      // screen vs the shared link a friend opened).
      const inViewMode = !!container.closest("[data-p2a-view]");
      const stepEl = container.closest("[data-p2a-step]");
      const source = inViewMode
        ? "share_view"
        : stepEl
          ? stepEl.getAttribute("data-p2a-step")
          : "unknown";

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign(
          {},
          window.instameExperiments || {},
          {
            event: "p2a_upsell_product_click",
            p2a_product_id: productId,
            p2a_product_handle: productHandle,
            p2a_product_url: href,
            p2a_click_target: clickTarget,
            p2a_style_name: container.dataset.p2aUpsellStyle || null,
            p2a_collection_handle:
              container.dataset.p2aUpsellCollection || null,
            p2a_source: source,
            // Whether the clicked tile was showing the shopper's own pet — lets us
            // compare click-through of personalized vs. plain upsell tiles.
            p2a_product_personalized: card
              ? card.dataset.tilePersonalized === "1"
              : false,
          }
        ));
      } catch (_) {}
      trackP2A("photo_to_art_upsell_product_click", {
        product_id: productId,
        product_handle: productHandle,
        click_target: clickTarget,
        source,
        style: container.dataset.p2aUpsellStyle || null,
        personalized: card ? card.dataset.tilePersonalized === "1" : false,
      });
    });
  }

  // ── Upsell render-recovery patch ─────────────────────────────────────
  //
  // Why this exists:
  //
  //   Horizon's <slideshow-component> registers an IntersectionObserver
  //   in its connectedCallback with `root: scroller, threshold: 0.7` and
  //   calls #updateVisibleSlides() from the observer callback. That method
  //   reads its private #visibleSlides set and writes `aria-hidden` on
  //   every slide based on that set — including overwriting the
  //   server-rendered aria-hidden="false" on slide 0.
  //
  //   When we inject the upsell grid via innerHTML, the scroller has no
  //   stable layout at the moment the observer's first callback fires
  //   (container queries on .card-gallery make this worse). The observer
  //   reports intersectionRatio < 0.7 for every slide, so #visibleSlides
  //   stays empty, and #updateVisibleSlides() writes aria-hidden="true"
  //   on slide 0. The CSS rule
  //     slideshow-component slideshow-slide:not([aria-hidden='false']) {
  //       content-visibility: hidden;
  //     }
  //   then hides every slide subtree — so the image element is in the
  //   DOM, the network has fetched it, but it never paints. Users see
  //   title + price + CTA but no product image. A window resize or
  //   devtools open triggers the slideshow's separate ResizeObserver,
  //   which re-runs #updateVisibleSlides() against a now-laid-out
  //   scroller and the images appear.
  //
  //   See playwright repro in commit history: at 16–100ms post-inject,
  //   visibleSlides drops from 10 → 0 and firstImg.getBoundingClientRect()
  //   .height stays at 0 even though img.complete and img.naturalHeight
  //   confirm the bytes are in memory.
  //
  // What this does:
  //   1. Force all lazy-loaded upsell images to eager. Defensive against
  //      Chrome's native lazy load skipping fetches when the image is
  //      inside a content-visibility:hidden subtree.
  //   2. Pin slide 0 of each slideshow to aria-hidden="false" and watch
  //      it via MutationObserver for ~2s. If the slideshow's own
  //      #updateVisibleSlides() flips it back to "true" while no other
  //      slide is "false", restore it.
  //
  // Returns counters so reportUpsellState can record how often the
  // recovery actually had to intervene in production.
  function recoverUpsellImages(grid) {
    const counters = {
      eagerSwaps: 0,
      ariaRestores: 0,
      slideshowsPatched: 0,
    };

    grid.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = "eager";
      counters.eagerSwaps++;
      // sizes="auto, …" requires the browser to know the rendered width
      // when picking a srcset entry. Inside an injected, possibly-hidden
      // subtree the rendered width is 0, so the browser may pick the
      // smallest source or skip the fetch entirely. Drop the auto
      // prefix and rely on the explicit sizes value (e.g.
      // "(min-width: 750px) 50vw, 100vw") that follows it.
      if (img.sizes && img.sizes.startsWith("auto")) {
        img.sizes = img.sizes.replace(/^auto,\s*/, "");
      }
    });

    const slideshows = grid.querySelectorAll("slideshow-component");
    slideshows.forEach((slideshow) => {
      const firstSlide = slideshow.querySelector("slideshow-slide");
      if (!firstSlide) return;
      counters.slideshowsPatched++;

      // Apply once immediately so the server-rendered initial visible
      // state survives the slideshow's first IntersectionObserver tick.
      firstSlide.setAttribute("aria-hidden", "false");

      // Keep watching: if the slideshow flips slide 0 back to "true"
      // *and* no sibling slide is "false", restore it. We only fight
      // back when *all* slides are hidden, so a user's deliberate swipe
      // to a later slide isn't undone.
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName !== "aria-hidden") continue;
          const target = /** @type {Element} */ (m.target);
          if (target !== firstSlide) continue;
          if (firstSlide.getAttribute("aria-hidden") === "false") continue;
          // Only restore if no sibling slide is currently visible. If
          // the slideshow legitimately advanced (e.g. user pressed
          // arrow), another slide will be aria-hidden="false" and we
          // leave it alone.
          const hasOtherVisible = !!slideshow.querySelector(
            'slideshow-slide[aria-hidden="false"]',
          );
          if (hasOtherVisible) continue;
          firstSlide.setAttribute("aria-hidden", "false");
          counters.ariaRestores++;
        }
      });
      observer.observe(slideshow, {
        attributes: true,
        attributeFilter: ["aria-hidden"],
        subtree: true,
      });
      // 2s is enough for the slideshow's ResizeObserver to fire on the
      // first stable layout pass. After that the IntersectionObserver
      // is reliable (we've seen it self-correct on the same callback
      // chain). Disconnect so we don't fight a user's swipe later.
      setTimeout(() => observer.disconnect(), 2000);
    });

    return counters;
  }

  // Fullscreen portrait lightbox. Clicking the result's Free Portrait image
  // opens a <dialog> (top layer) showing the full-size portrait with an X and a
  // dark backdrop — same look as the cart "View File" modal, but big. The
  // content is a real <img> so iOS long-press -> "Save to Photos" works. The
  // trigger lives on the JS-injected tile, so listeners are delegated.
  function bindLightbox() {
    const dialog = document.querySelector("[data-p2a-lightbox]");
    if (!dialog) return;
    const img = dialog.querySelector("[data-p2a-lightbox-img]");
    const closeBtn = dialog.querySelector("[data-p2a-lightbox-close]");

    // Bumped on every open/close so a master that finishes downloading after
    // the lightbox closed (or reopened on a different portrait) can't clobber
    // img.src with a stale image.
    let loadToken = 0;

    const openLightbox = (src, previewSrc) => {
      if (!src) return;
      const token = ++loadToken;
      if (previewSrc && previewSrc !== src) {
        // Progressive reveal: the `?v=display` copy is already warm in the
        // HTTP cache (it's the trigger tile's own <img>), so paint it
        // immediately and swap in the full-quality master once it arrives.
        img.src = previewSrc;
        const master = new Image();
        // Match the lightbox <img>'s crossorigin="anonymous" so the preload
        // shares its cache entry — a no-CORS preload would be stored without
        // Access-Control-Allow-Origin and poison the swap.
        master.crossOrigin = "anonymous";
        master.onload = () => {
          if (token === loadToken) img.src = src;
        };
        master.src = src;
      } else {
        img.src = src;
      }
      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      trackP2A("photo_to_art_portrait_enlarged", {});
    };
    const closeLightbox = () => {
      loadToken++;
      if (typeof dialog.close === "function" && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
      }
      img.src = "";
    };

    document.addEventListener("click", (e) => {
      const trigger = e.target.closest && e.target.closest("[data-p2a-enlarge]");
      if (!trigger) return;
      e.preventDefault();
      openLightbox(
        trigger.getAttribute("data-p2a-enlarge"),
        trigger.getAttribute("data-p2a-enlarge-preview"),
      );
    });
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const trigger = e.target.closest && e.target.closest("[data-p2a-enlarge]");
      if (!trigger) return;
      e.preventDefault();
      openLightbox(
        trigger.getAttribute("data-p2a-enlarge"),
        trigger.getAttribute("data-p2a-enlarge-preview"),
      );
    });

    closeBtn.addEventListener("click", closeLightbox);
    // Click on the dialog area outside the image closes it.
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) closeLightbox();
    });
    // Native <dialog> fires "close" on Escape; clear the image when it does
    // (and invalidate any in-flight master swap — Escape skips closeLightbox).
    dialog.addEventListener("close", () => {
      loadToken++;
      img.src = "";
    });
  }

  // Inject the free portrait as the FIRST cell of the product grid so it reads
  // as one of the products: a normal-looking card whose title is "Digital
  // Portrait" and whose price/CTA slots become Download/Share. Triggered from
  // applyHTML on every grid render. Idempotent, and a no-op until a portrait
  // exists, so the generating-step prefetch (no resultId yet) never gets the
  // tile.
  function injectFreePortraitTile(grid, container) {
    // GRADUATED: the portrait IS the first product-grid tile, for everyone.
    // This used to return early unless the exp_p2a_result_grid flag read "test".
    if (!state.resultId) return;
    if (!container || !container.closest('[data-p2a-step="result"]')) return;

    const ul = grid.querySelector("ul.product-grid");
    if (!ul) return;
    if (ul.querySelector("[data-p2a-free-tile]")) return; // already injected

    // Tile <img> shows the `?v=display` copy — the same URL as the result
    // <img>, so it paints straight from cache. The enlarge target stays the
    // full-quality master so the lightbox (and iOS long-press "Save to
    // Photos" inside it) delivers full resolution; the display copy rides
    // along as the lightbox's instant preview while the master loads.
    const imgUrl = `${API_BASE}/api/photo-to-art/image/${state.resultId}`;
    const displayUrl = `${imgUrl}?v=display`;
    const li = document.createElement("li");
    li.className = "product-grid__item p2a__free-li";
    li.setAttribute("data-p2a-free-tile", "");
    li.innerHTML = `
      <div class="p2a__free-card">
        <div class="p2a__free-media" data-p2a-enlarge="${imgUrl}" data-p2a-enlarge-preview="${displayUrl}" role="button" tabindex="0" aria-label="Enlarge portrait">
          <span class="p2a__free-badge">Gift</span>
          <img class="p2a__free-img" src="${displayUrl}" alt="Your free pet portrait">
          <span class="p2a__free-expand" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
          </span>
        </div>
        <p class="p2a__free-title">Digital Portrait</p>
        <p class="p2a__free-price">Free</p>
        <div class="p2a__free-actions">
          <button type="button" class="p2a__free-download" data-p2a-free-download>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download
          </button>
          <button type="button" class="p2a__free-share" data-p2a-free-share>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            <span data-p2a-free-share-label>Share</span>
          </button>
        </div>
      </div>`;

    // Call the SAME functions the control buttons run — no proxy. Download is
    // the identical downloadResult(); Share is the identical native share, with
    // a copy-link fallback (most desktops) that confirms on the tile's own
    // label so the user gets visible feedback.
    li.querySelector("[data-p2a-free-download]").addEventListener("click", (e) => {
      e.preventDefault();
      downloadResult();
    });
    li.querySelector("[data-p2a-free-share]").addEventListener("click", (e) => {
      e.preventDefault();
      if (navigator.share) {
        shareResultNative();
      } else {
        copyShareLink(li.querySelector("[data-p2a-free-share-label]"));
        trackP2A("photo_to_art_shared", { platform: "copy_link", style: state.styleName });
      }
    });

    ul.insertBefore(li, ul.firstElementChild);
  }

  async function loadUpsell(container, styleName) {
    const grid = container.querySelector("[data-p2a-upsell-grid]");
    if (!grid) return;

    const map = getStyleToCollectionMap();
    const collectionHandle =
      (styleName && map[styleName]) || "best-sellers";

    attachUpsellClickTracking(container, styleName, collectionHandle);

    // Diagnostic: detect intermittent "no product images" reports. Counts
    // server-rendered cards vs. gallery blocks both immediately after
    // injection and after a frame (to catch slideshow upgrade timing). If
    // gallery_count < product_count → server stripped the nested static
    // block; if counts match but img height is 0 → custom-element/CV race.
    // Fires `photo_to_art_upsell_rendered` for PostHog querying. `recovery`
    // carries counters from recoverUpsellImages so we can confirm in prod
    // that the patch is firing when needed (and how often).
    const reportUpsellState = (html, source, recovery) => {
      try {
        // Count only real product cards. The injected free-portrait tile
        // (.p2a__free-li) is a .product-grid__item but deliberately has no
        // .card-gallery, so counting it made `galleries < productItems`
        // permanently true once the result-grid experiment graduated to 100%
        // — masking the genuine failure this detector exists to catch (a
        // stripped nested block, which shows up as equal counts + 0 height).
        const productItems = grid.querySelectorAll(
          ".product-grid__item:not(.p2a__free-li)",
        ).length;
        const galleries = grid.querySelectorAll(".card-gallery").length;
        const slideshows = grid.querySelectorAll("slideshow-component").length;
        const slideshowDefined = !!customElements.get("slideshow-component");
        // Layout read only on the deferred check — at sync time the
        // slideshow hasn't laid out yet and reading the height would force
        // a synchronous reflow with an unreliable result.
        const measureLayout = source.endsWith("-deferred");
        const firstImg = measureLayout
          ? grid.querySelector(".card-gallery img")
          : null;
        const firstImgHeight = firstImg
          ? Math.round(firstImg.getBoundingClientRect().height)
          : null;
        const firstImgComplete = firstImg ? firstImg.complete : null;
        // Count first-slide aria-hidden mismatches: if any slideshow has zero
        // slides with aria-hidden="false" after layout has settled, the bug
        // is happening (or rather, the recovery patch had to step in).
        let slideshowsWithVisibleSlide = null;
        if (measureLayout) {
          slideshowsWithVisibleSlide = Array.from(
            grid.querySelectorAll("slideshow-component"),
          ).filter((s) =>
            s.querySelector('slideshow-slide[aria-hidden="false"]'),
          ).length;
        }
        const broken =
          productItems > 0 &&
          (galleries < productItems ||
            (measureLayout && firstImgHeight === 0));

        if (broken) {
          console.warn("[photo-to-art] upsell render anomaly", {
            collection: collectionHandle,
            source,
            productItems,
            galleries,
            slideshows,
            slideshowDefined,
            firstImgHeight,
            firstImgComplete,
            slideshowsWithVisibleSlide,
            recovery,
          });
        }

        trackP2A("photo_to_art_upsell_rendered", {
          collection_handle: collectionHandle,
          style_name: styleName || null,
          source,
          product_items: productItems,
          galleries,
          slideshow_count: slideshows,
          slideshow_defined: slideshowDefined,
          first_img_height: firstImgHeight,
          first_img_complete: firstImgComplete,
          response_length: typeof html === "string" ? html.length : null,
          slideshows_with_visible_slide: slideshowsWithVisibleSlide,
          recovery_eager_swaps: recovery ? recovery.eagerSwaps : null,
          recovery_aria_restores: recovery ? recovery.ariaRestores : null,
          recovery_slideshows_patched: recovery ? recovery.slideshowsPatched : null,
          broken,
        });
      } catch (e) {
        console.warn("[photo-to-art] upsell instrumentation failed", e);
      }
    };

    const applyHTML = (html, source) => {
      grid.innerHTML = html;
      grid.classList.remove("is-skeleton");
      // Disable hover image cycling on P2A upsell cards — the on:pointer* attributes
      // drive the Shopify component event system; removing them prevents
      // previewImage/resetImage from firing on these cards.
      grid.querySelectorAll(".card-gallery").forEach((/** @type {Element} */ el) => {
        el.removeAttribute("on:pointerenter");
        el.removeAttribute("on:pointerleave");
      });
      // Apply the image-recovery patch synchronously so it's in place before
      // the slideshow custom elements run their first IntersectionObserver
      // callback (which otherwise hides every slide — see comment inside
      // recoverUpsellImages for the full root-cause analysis).
      const recovery = recoverUpsellImages(grid);

      // Personalize the freshly-injected tiles. prewarm fetches base configs +
      // decodes base mockups now (cheap, user-independent); personalize
      // composites any tiles whose style the user already has. Styles generated
      // during this visit arrive a moment later via the p2a:design-ready event.
      // Pet context first: tiles must only personalize with the pet currently
      // in the wizard — without it, a shopper who switched pets sees the
      // PREVIOUS pet on the upsell (the styles map spans all their uploads).
      try {
        if (window.TilePersonalizer) {
          if (window.TilePersonalizer.setPetContext) {
            window.TilePersonalizer.setPetContext(state.uploadId || null);
          }
          window.TilePersonalizer.prewarm(grid);
          window.TilePersonalizer.personalize(grid);
        }
      } catch (e) {}

      // Slot the free portrait in as the first product tile (result step only).
      try {
        injectFreePortraitTile(grid, container);
      } catch (e) {}
      // First check is synchronous (sees DOM right after innerHTML); a
      // second check after rAF + a short delay catches the case where the
      // slideshow custom element upgrades and reveals slides only later.
      reportUpsellState(html, source + "-sync", recovery);
      requestAnimationFrame(() => {
        setTimeout(() => reportUpsellState(html, source + "-deferred", recovery), 300);
      });
    };

    // Already fetched — apply instantly.
    if (_upsellCache.has(collectionHandle)) {
      applyHTML(_upsellCache.get(collectionHandle), "cache");
      return;
    }

    // Already fetching for this handle — piggyback on the in-flight promise.
    if (_upsellInFlight.has(collectionHandle)) {
      try {
        applyHTML(await _upsellInFlight.get(collectionHandle), "inflight");
      } catch (e) {
        container.hidden = true;
      }
      return;
    }

    // Fetch the full alternate-template page rather than Shopify's
    // Section Rendering API (?section_id= / ?sections=) — both of those
    // strip nested static blocks (gallery, title, price), leaving only
    // the bare "Shop Now" CTA. Full page load renders correctly.
    const url = `/collections/${encodeURIComponent(collectionHandle)}?view=p2a-upsell`;
    const fetchPromise = (async () => {
      const res = await fetch(url, { headers: { Accept: "text/html" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const fullHTML = await res.text();
      const doc = new DOMParser().parseFromString(fullHTML, "text/html");
      // Section ID differs by render path: Section Rendering API uses
      // `shopify-section-p2a-upsell-grid`; full page uses
      // `shopify-section-template--<id>__p2a-upsell-grid`. Match by class
      // (declared in the section schema) to handle both.
      const sectionEl = doc.querySelector(".p2a-upsell-grid-section");
      if (!sectionEl) {
        throw new Error("upsell section missing in response");
      }
      return sectionEl.outerHTML;
    })();
    _upsellInFlight.set(collectionHandle, fetchPromise);

    try {
      const html = await fetchPromise;
      _upsellCache.set(collectionHandle, html);
      applyHTML(html, "fetch");
    } catch (e) {
      console.warn("[photo-to-art] upsell load failed", e);
      container.hidden = true;
    } finally {
      _upsellInFlight.delete(collectionHandle);
    }
  }
})();
